/**
 * ClipEditorModal
 *
 * Full-screen modal for recording a voiceover clip over 1–2 video sources.
 * The modal captures a composite screengrab (video panels + annotation drawings)
 * combined with microphone audio into a single .webm blob.
 *
 * Each video panel has its own independent annotation toolbar (brush / eraser / colors),
 * enabled only while that panel's video is paused.
 */

import React, { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import type { NotableSegment, VoiceoverNotableSegmentClip } from '../types/videoEdit';
import type { DrawColor } from './VideoCanvasOverlay';
import {
  EBTN_PRIMARY,
  EBTN_SECONDARY,
  EBTN_GHOST,
  EBTN_ICON,
  EBTN_TRANSPORT,
  EBTN_SWATCH_TARGET,
  EBTN_DESTRUCTIVE,
  EBTN_RECORD,
  EBTN_SEGMENTED,
} from './videoEditor/editorButtonStyles';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ClipSource {
  videoUrl: string;
  segment?: NotableSegment; // if set, playback is constrained to segment bounds
  label?: string;
  /** When true, uses the user's camera as the video source (no video file) */
  isCameraSource?: boolean;
  /** Visual rotation applied to this source's video (0 | 90 | 180 | 270). Default: 0. */
  rotation?: number;
}

export interface ClipEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when user clicks "Save & Close" */
  onSave: (clip: VoiceoverNotableSegmentClip) => void;
  /** Called when user clicks "Save & Continue" — modal stays open for another recording */
  onSaveAndContinue: (clip: VoiceoverNotableSegmentClip) => void;
  primarySource: ClipSource;
  secondarySource?: ClipSource;
  /**
   * When true, renders inline (filling its parent container) instead of as a
   * fixed full-screen overlay. Used inside LessonVideoEditor so the clip editor
   * sits alongside the Lesson Organizer panel.
   */
  embedded?: boolean;
  /** Dev preview: start in recording mode (shows Save & Continue / Save & Close / Abort) */
  initiallyRecording?: boolean;
  /**
   * When provided and no secondarySource is set, shows an "Add video source"
   * button in the header so the user can add a side-by-side comparison source
   * without going back through the picker flow.
   */
  onAddSecondarySource?: () => void;
}

type DrawingTool = 'brush' | 'eraser' | 'none';
const SPEEDS = [1, 0.5, 0.25] as const;
type Speed = (typeof SPEEDS)[number];

// ── Constants ──────────────────────────────────────────────────────────────────

/** Composite canvas panel dimensions (16:9) — 720p per panel */
const PANEL_W = 1280;
const PANEL_H = 720;
/**
 * Longest edge of a single-source composite. The canvas is sized to the
 * source's own aspect rather than to a fixed landscape frame, so a portrait
 * video fills it instead of being object-contained inside 1280x720 and having
 * 68.4% of every frame painted black. Nothing re-encodes downstream, so those
 * bars were permanent and the player's object-contain added a second set.
 *
 * Capping the long edge keeps the pixel count in the same range: 16:9 still
 * lands on exactly 1280x720, and the worst case (4:3 or 3:4) is 1280x960,
 * a third more pixels rather than a different order of magnitude.
 */
const PANEL_LONG_EDGE = 1280;

/**
 * Keeps the whole bubble inside its box, not just its centre.
 *
 * The bubble's diameter is 10% of the box width, so its radius is 0.05 in
 * normalized x. In y the same radius is a different fraction, because the box
 * is not square, which is why the two axes clamp differently. The old 0.05 to
 * 0.95 on both axes clamped the centre and let half the bubble hang out.
 */
/**
 * Bubble diameter as a fraction of the box width. The single source of truth:
 * the on-screen element, the recorded radius and the edge clamp all derive
 * from it, so the framed bubble and the recorded one cannot drift apart.
 *
 * 0.15 rather than 0.10 because of the surface these are watched on. A 16:9
 * clip on a 390pt phone renders about 58pt across at 0.15; at 0.10 it was
 * about 39pt, too small to read a face.
 */
/**
 * Diameter as a fraction of one panel — not of the pair.
 *
 * It used to be a fraction of whatever box the bubble floated over, which in
 * side by side is both panels, so the paired bubble rendered at twice the
 * single-source size. Dividing by the panel makes the composite draw a 192px
 * bubble in both layouts — identical in the delivered file — and returns single
 * source to exactly what it was.
 *
 * The paired preview is smaller in absolute pixels because a paired panel is
 * displayed at half width. That is accurate to what records, not too small.
 */
const PIP_DIAMETER_FRACTION = 0.15;
/**
 * Single source gets a bigger bubble than a paired panel.
 *
 * In side by side the bubble floats over one of two videos and is competing
 * for the coach's attention with the other; at 0.15 it is the right weight.
 * Single source has no competition and the same fraction reads as an
 * afterthought, so it gets 0.22.
 */
const PIP_DIAMETER_FRACTION_SINGLE = 0.22;

/**
 * The bubble's diameter, from a panel's own dimensions.
 *
 * Keyed off the panel's LONGER edge, not its width. The width was fine for
 * landscape and quietly wrong for everything else: a 9:16 clip composites to a
 * 720x1280 frame, so 15% of the width drew a 108px bubble where a 16:9 clip of
 * the same lesson drew 192px. Same constant, same coach, nearly half the size,
 * for no reason other than the source being portrait. `compositeSizeFor`
 * normalises the long edge to PANEL_LONG_EDGE, so keying off the long edge
 * makes the recorded bubble one size in every aspect.
 *
 * Both the on-screen bubble and the composite call this with their own panel
 * dimensions, which is what keeps the preview honest about what records.
 */
function pipDiameterFor(panelW: number, panelH: number, paired: boolean): number {
  const fraction = paired ? PIP_DIAMETER_FRACTION : PIP_DIAMETER_FRACTION_SINGLE;
  return Math.max(panelW, panelH) * fraction;
}
/** Default inset from the bottom and right edges, and the clamp's margin. */
const PIP_EDGE_INSET_PX = 16;

/**
 * Keeps the bubble wholly inside the box, off its edges, and off the divider.
 *
 * Bounds are on the bubble's edges rather than its centre, so no part of it can
 * leave the picture. The seam is not out of bounds: the coach drags the bubble
 * and decides where it belongs, including between the two panels.
 *
 * Both the preview and the composite run positions through this, which is what
 * keeps them agreeing wherever the coach drags to.
 */
function clampPip(
  x: number,
  y: number,
  boxW: number,
  boxH: number,
  diameter: number,
): { x: number; y: number } {
  const rx = boxW > 0 ? (diameter / 2 + PIP_EDGE_INSET_PX) / boxW : 0.5;
  const ry = boxH > 0 ? (diameter / 2 + PIP_EDGE_INSET_PX) / boxH : 0.5;
  const cx = rx * 2 >= 1 ? 0.5 : Math.max(rx, Math.min(1 - rx, x));
  const cy = ry * 2 >= 1 ? 0.5 : Math.max(ry, Math.min(1 - ry, y));
  return { x: cx, y: cy };
}

/** VP9 requires even dimensions. */
const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);

/**
 * Composite size for one source. Falls back to the old fixed frame when the
 * video has not reported its dimensions yet.
 */
function compositeSizeFor(vw: number, vh: number): { w: number; h: number } {
  if (!vw || !vh) return { w: PANEL_W, h: PANEL_H };
  const scale = PANEL_LONG_EDGE / Math.max(vw, vh);
  return { w: even(vw * scale), h: even(vh * scale) };
}

/**
 * Tolerance used in the startTime boundary check inside handleTimeUpdate.
 * After video.play() is called with currentTime exactly at segStart, the browser
 * fires timeupdate almost immediately.  Floating-point representation means
 * video.currentTime can be a tiny fraction below seg.startTime even though they
 * are "the same" position.  Without this epsilon the boundary check fires
 * `video.currentTime = seg.startTime`, which aborts the pending play() promise
 * (per the HTML spec, setting currentTime while play is pending causes AbortError).
 * 50 ms is imperceptible and well within the accuracy of the range input (step 0.05 s).
 */
const SEGMENT_START_EPSILON = 0.05;

// Brush/eraser sizes are in canvas coordinates.
// At 1280×720 canvas displayed ~640px wide on-screen, the scale factor is ~2×,
// so these values are doubled from the old 640×360 defaults to maintain the same
// visual stroke width when the user draws.
const BRUSH_SIZE = 10;
const ERASER_SIZE = 40;

/**
 * Every colour the pen can paint. Complete, and unchanged.
 *
 * This is the render map, not the picker, and the two are deliberately
 * separate. Nothing persisted reaches this map — a stroke drawn here is baked
 * into pixels, and the only index is local `drawColor` state. Persisted
 * strokes carry a colour *name* (`color: DrawColor`) and are resolved by
 * VideoCanvasOverlay against DRAW_COLOR_HEX in drawColors.ts, which still
 * holds all six and is untouched.
 *
 * It stays total anyway. `Record<DrawColor, string>` is a promise the type
 * system checks, and narrowing it to whatever the picker currently offers
 * would silently make the map partial the next time something does index it
 * with a stored value.
 */
const COLOR_HEX: Record<DrawColor, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  white: '#ffffff',
  black: '#111827',
};

/**
 * What a coach can choose. Four, in order of how well they hold up on video.
 *
 * Black is gone because #111827 is all but invisible both on this toolbar and
 * in the shadowed half of any outdoor frame. Blue is gone because #3b82f6 is
 * the closest of the six to a hard court and to the editor's own accent, so a
 * blue stroke reads as chrome rather than as coaching. Neither removal touches
 * COLOR_HEX, so a draft that used either still renders exactly as before.
 */
const DRAW_COLORS: { value: DrawColor; hex: string; label: string }[] = [
  { value: 'red', hex: COLOR_HEX.red, label: 'Red' },
  { value: 'yellow', hex: COLOR_HEX.yellow, label: 'Yellow' },
  { value: 'green', hex: COLOR_HEX.green, label: 'Green' },
  { value: 'white', hex: COLOR_HEX.white, label: 'White' },
];

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/** Shows tenths of a second — used for the in-panel timestamp watermark */
function formatTimeDetailed(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const whole = Math.floor(sec).toString().padStart(2, '0');
  const frac = Math.floor((sec % 1) * 10);
  return `${m}:${whole}.${frac}`;
}

type StoredAnnotation = { timestamp: number; imageData: ImageData };
/** One undo step: the annotation at `timestamp` before a stroke, or null if there was none. */
type HistoryEntry = { timestamp: number; imageData: ImageData | null };
const MAX_UNDO_STEPS = 10;
const ANNOTATION_DURATION = 1.0; // seconds — how long each annotation is visible on the timeline

/**
 * Return the annotation whose 1s window covers `time`, preferring the most
 * recent one when multiple annotations overlap (timestamps within 1s of each other).
 */
function findActiveAnnotation(
  annotations: StoredAnnotation[],
  time: number
): StoredAnnotation | null {
  const inRange = annotations.filter(
    (a) => time >= a.timestamp && time < a.timestamp + ANNOTATION_DURATION
  );
  if (inRange.length === 0) return null;
  // Most recent timestamp wins if multiple are in range
  return inRange.reduce((best, a) => (a.timestamp > best.timestamp ? a : best));
}

/**
 * Apply speed and mute state to a video element in one atomic operation.
 * Always mutes at 0.5× and below (pitched-down audio is unintelligible and
 * browsers cannot play it cleanly), and also respects the user's explicit
 * per-panel mute preference.
 * Keeping this as a single call (instead of separate muted/playbackRate
 * assignments spread across event handlers) eliminates the race condition where
 * handleSeeking muted the video and handleSeeked tried to restore state at an
 * unpredictable time relative to the play() call.
 */
function applyVideoState(video: HTMLVideoElement, speed: Speed, muted: boolean) {
  video.muted = muted || speed < 1;
  video.playbackRate = speed;
}

// ── VideoPanel sub-component ───────────────────────────────────────────────────


/**
 * The drawing tools.
 *
 * One instance serves both layouts: inside a panel when there is a single
 * source, and once beneath both panels when there are two. The tool, the
 * colour and the history buttons are the parent's; which canvas a stroke lands
 * on is still decided by the pointer, not here.
 *
 * `children` is the per-panel utility cluster (speed, mute) that the
 * single-source layout keeps in this row. Side by side passes none — those
 * controls belong to a panel and move into each transport.
 */
function DrawToolbar({
  drawingTool, setDrawingTool, drawColor, setDrawColor,
  canAnnotate, undoDepth, redoDepth, onUndo, onRedo, fullWidth = false, children,
}: {
  drawingTool: DrawingTool;
  setDrawingTool: React.Dispatch<React.SetStateAction<DrawingTool>>;
  drawColor: DrawColor;
  setDrawColor: (c: DrawColor) => void;
  canAnnotate: boolean;
  undoDepth: number;
  redoDepth: number;
  onUndo: () => void;
  onRedo: () => void;
  fullWidth?: boolean;
  children?: React.ReactNode;
}) {
  const canAnnotateEffective = canAnnotate;
  return (
    <div
      /* Paired: a pill that shrinks to its content and sits centred under the
         pair, on a surface one step above the panels so it reads as a control
         rather than as an empty band. Single source keeps the full-width chip
         it has always had, because it also carries speed and mute. */
      className={`flex items-center flex-shrink-0 ${fullWidth ? 'justify-center' : 'w-full justify-between'}`}
      style={{
        background: fullWidth ? 'var(--editor-surface-raised)' : 'var(--editor-surface)',
        borderRadius: fullWidth ? '100px' : '8px',
        padding: '0 12px',
        height: '36px',
        margin: '4px 0',
      }}
    >
        <div className="flex items-center gap-1">
          {/* Draw label */}
          {/* Brush and eraser are mutually exclusive, so they are one control
              rather than two loose glyphs. Track 32 inside a 36 row, so the row
              does not grow. Both still toggle back to 'none' on a second press,
              which is why the group can be all-unchecked. */}
          <div
            role="radiogroup"
            aria-label="Drawing tool"
            className={EBTN_SEGMENTED.track}
          >
            <button
              type="button"
              role="radio"
              aria-checked={drawingTool === 'brush'}
              onClick={() => setDrawingTool((t) => (t === 'brush' ? 'none' : 'brush'))}
              disabled={!canAnnotateEffective}
              className={drawingTool === 'brush' ? EBTN_SEGMENTED.iconItemSelected : EBTN_SEGMENTED.iconItem}
              title={canAnnotateEffective ? 'Draw on video' : 'Pause video to annotate'}
              aria-label="Draw on video"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3zm13.71-9.37-1.34-1.34a1 1 0 0 0-1.41 0L9 12.25 11.75 15l8.96-8.96a1 1 0 0 0 0-1.41z" />
              </svg>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={drawingTool === 'eraser'}
              onClick={() => setDrawingTool((t) => (t === 'eraser' ? 'none' : 'eraser'))}
              disabled={!canAnnotateEffective}
              className={drawingTool === 'eraser' ? EBTN_SEGMENTED.iconItemSelected : EBTN_SEGMENTED.iconItem}
              title={canAnnotateEffective ? 'Erase drawing' : 'Pause video to annotate'}
              aria-label="Erase drawing"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.14 3c-.51 0-1.02.2-1.41.59L2.59 14.73c-.78.77-.78 2.04 0 2.83L5.03 20H20v-2h-8.56l7.97-7.97c.78-.78.78-2.05 0-2.83l-2.85-2.85A2 2 0 0015.14 3z" />
              </svg>
            </button>
          </div>

          {/* Undo and redo, where clear-all used to be. Repeated undo reaches
              the same empty canvas without a single irreversible press, so the
              destructive control is gone rather than relocated.

              mx-1.5, not mx-1: at mx-1 the eraser's 44 target and undo's 44
              target overlapped by 2px, and the overlap belonged to undo. */}
          <div className="w-px h-4 mx-1.5" style={{ background: 'var(--editor-border)' }} />
          <button
            type="button"
            onClick={onUndo}
            disabled={undoDepth === 0}
            className={EBTN_ICON}
            title="Undo"
            aria-label="Undo"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onRedo}
            disabled={redoDepth === 0}
            className={EBTN_ICON}
            title="Redo"
            aria-label="Redo"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.06-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" />
            </svg>
          </button>

          {/* Color swatches — only visible when brush is active */}
          {drawingTool === 'brush' && canAnnotateEffective && (
            <>
              <div className="w-px h-4 mx-1" style={{ background: 'var(--editor-border)' }} />
              {/* gap-3, because EBTN_SWATCH_TARGET reaches 6px past each
                  swatch and a 12px gap is exactly what keeps one swatch's
                  target out of its neighbour's. */}
              <div role="radiogroup" aria-label="Pen colour" className="flex items-center gap-3">
                {DRAW_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    role="radio"
                    aria-checked={drawColor === c.value}
                    aria-label={c.label}
                    onClick={() => setDrawColor(c.value)}
                    className={`${EBTN_SWATCH_TARGET} w-5 h-5 rounded-full border-2 transition-transform cursor-pointer ${
                      drawColor === c.value
                        ? 'border-white scale-110'
                        : 'border-[var(--editor-border)] hover:border-[var(--editor-text-muted)]'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={c.label}
                  />
                ))}
              </div>
            </>
          )}
      </div>
      {children}
    </div>
  );
}

const VideoPanel = forwardRef<VideoPanelHandle, VideoPanelProps>(
  (props, ref) => VideoPanelInner(props, ref),
);
VideoPanel.displayName = 'VideoPanel';


/**
 * The end of a panel's playable range.
 *
 * There used to be two answers to this in one file. The transport's scrubber
 * used `segment?.endTime ?? duration`; the play handler used
 * `segment?.endTime ?? Infinity`. Both were locally defensible and they meant
 * different things, which is the kind of divergence that is fine right up
 * until someone changes one of them.
 *
 * One meaning now: the segment's end, or failing that the end of the video.
 * `duration` is the honest answer for both — a source with no segment is
 * scrubbable to its end, and reaching that end is reaching the end.
 *
 * The case that needs care is duration not being known yet. Before metadata
 * loads it is 0 or NaN, and returning that would make `currentTime >= segEnd`
 * true at rest, so every play click would re-seek to the start. Infinity is
 * the safe reading of "no end known yet": never treat the clip as finished.
 */
function segmentEndOf(segEnd: number | null, duration: number): number {
  if (segEnd !== null) return segEnd;
  return Number.isFinite(duration) && duration > 0 ? duration : Infinity;
}

/** Speed and mute. Belongs to a panel, never to the shared drawing tools. */
function PanelUtilities({ showVideoControls, speed, onSpeedChange, isMuted, onToggleMute }: {
  showVideoControls: boolean;
  speed: Speed;
  onSpeedChange: (s: Speed) => void;
  isMuted: boolean;
  onToggleMute: () => void;
}) {
  return (
        <div className="flex items-center gap-2">
          {showVideoControls && (
            <div
              role="radiogroup"
              aria-label="Playback speed"
              className={EBTN_SEGMENTED.track}
            >
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={speed === s}
                  onClick={() => onSpeedChange(s)}
                  className={speed === s ? EBTN_SEGMENTED.itemSelected : EBTN_SEGMENTED.item}
                >
                  {s}×
                </button>
              ))}
            </div>
          )}
          {/* Muted is red, and the glyph carries waves when it is not, so the
              on state is legible on its own rather than only against the off
              one. The colour is inline because it has to beat the recipe's,
              and two arbitrary-value classes of the same property resolve by
              emission order rather than by class order. */}
          <button
            type="button"
            onClick={onToggleMute}
            className={EBTN_ICON}
            style={isMuted ? { color: 'var(--editor-danger)' } : undefined}
            aria-pressed={isMuted}
            title={isMuted ? 'Unmute audio' : 'Mute audio'}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </button>
        </div>
  );
}

/**
 * One panel's playback controls: play, scrubber, timecode, speed, mute.
 *
 * A component rather than JSX inside VideoPanel because side by side renders
 * both of these in the parent, below the shared drawing tools — the tools
 * belong nearer the video than the transport does. Single source still renders
 * it inside its own panel, unchanged.
 */
function PanelTransport({
  showVideoControls, sideBySide, isPlaying, onTogglePlay,
  segStart, segEnd, currentTime, onSeek, onScrubStart, onScrubEnd,
  speed, onSpeedChange, isMuted, onToggleMute,
}: {
  showVideoControls: boolean;
  sideBySide: boolean;
  isPlaying: boolean;
  onTogglePlay: () => void;
  segStart: number;
  segEnd: number;
  currentTime: number;
  onSeek: (t: number) => void;
  onScrubStart: () => void;
  onScrubEnd: () => void;
  speed: Speed;
  onSpeedChange: (s: Speed) => void;
  isMuted: boolean;
  onToggleMute: () => void;
}) {
  return (
    <>
      {!showVideoControls && sideBySide && <div className="h-9 flex-shrink-0" aria-hidden="true" />}

      {/* Speed + Playback controls — hidden for blank page and freeze frame */}
      {showVideoControls && (
        <div className={`flex items-center flex-shrink-0 ${sideBySide ? 'gap-3 h-9' : 'gap-2'}`}>
          <button
            type="button"
            onClick={onTogglePlay}
            /* No inline background: that was why this button had no hover. An
               inline style beats every stylesheet rule short of !important, so
               .ebtn-icon:hover could never repaint it. The resting fill now
               lives on the pseudo-element that also carries the hover. */
            className={EBTN_TRANSPORT}
            title={isPlaying ? 'Pause' : 'Play'}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <input
            type="range"
            min={segStart}
            max={segEnd}
            step={0.05}
            value={Math.min(Math.max(currentTime, segStart), segEnd)}
            onChange={(e) => onSeek(parseFloat(e.target.value))}
            /* Both pointer and keyboard. A range input is focusable and arrow
               keys move it without ever firing a pointer event, so pointer
               handlers alone would leave keyboard scrubbing fighting playback
               exactly as the drag used to. onKeyUp/onBlur close it. */
            onPointerDown={onScrubStart}
            onPointerUp={onScrubEnd}
            onPointerCancel={onScrubEnd}
            onKeyDown={onScrubStart}
            onKeyUp={onScrubEnd}
            onBlur={onScrubEnd}
            className="flex-1 h-1 accent-db-accent"
          />
          {/* "in segment", because segEnd is the segment's end and a coach who
              has been watching a 9:11 source otherwise reads this as the video
              having changed length. */}
          {/* The numbers are the flex child, so `items-center` centres them on
              the row and they line up with the scrubber. The qualifier is taken
              out of flow beneath them, which is the only way it can sit below
              without pushing them up. It overhangs the row by about 6, into the
              panel container's own 16 of bottom padding, so nothing clips it
              and nothing moves. */}
          {/* The scrubber is segment-scoped, so the segment-relative time is
              what it points at and leads here. The absolute position sits under
              it, quieter, for anyone matching against the source video. No
              label: two numbers in two weights say which is which. No segment
              boundary is computed here — segStart and segEnd are the same values
              the scrubber already uses. */}
          <span className="relative flex-shrink-0 whitespace-nowrap text-xs leading-[14px] font-mono text-[var(--editor-text)]">
            {formatTime(Math.max(0, currentTime - segStart))} / {formatTime(Math.max(0, segEnd - segStart))}
            <span className="absolute right-0 top-full text-[10px] leading-3 font-normal text-[var(--editor-text-muted)]">
              {formatTime(currentTime)}
            </span>
          </span>
          {sideBySide && <PanelUtilities showVideoControls={showVideoControls} speed={speed} onSpeedChange={onSpeedChange} isMuted={isMuted} onToggleMute={onToggleMute} />}
        </div>
      )}
    </>
  );
}

interface VideoPanelProps {
  source: ClipSource;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  annotationCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  speed: Speed;
  isMuted: boolean;
  onTogglePlay: () => void;
  onSeek: (t: number) => void;
  onScrubStart: () => void;
  onScrubEnd: () => void;
  onSpeedChange: (s: Speed) => void;
  onToggleMute: () => void;
  /** Optional overlay element rendered inside the video area (e.g. PiP camera circle) */
  pipOverlay?: React.ReactNode;
  /**
   * True when this panel is one of a pair.
   *
   * Playback is per-panel — each video has its own play, seek, speed and mute,
   * which is the point of comparing two clips — so a single shared transport
   * is not available. Instead the chrome a panel does not need is reserved as
   * empty space, so both videos get the same box. Off in the single-source
   * case, where reserving anything would only cost video height.
   */
  sideBySide?: boolean;
  /** Reports the drawn picture's height, so the parent can match the divider to it. */
  onVideoBoxHeight?: (h: number) => void;
  /**
   * The tool and colour, owned by the parent so both panels share one control.
   * Which canvas a stroke lands on is still decided by the pointer, not by
   * this — nothing about when or where a stroke commits changes.
   */
  drawingTool: DrawingTool;
  drawColor: DrawColor;
  onSetDrawingTool: React.Dispatch<React.SetStateAction<DrawingTool>>;
  onSetDrawColor: (c: DrawColor) => void;
  /** Called after a stroke is committed, so the parent can record the order. */
  onStrokeCommitted?: () => void;
}

/** What the parent can drive from the shared Undo and Redo. */
export interface VideoPanelHandle {
  /** Returns false when there was nothing left to step back to. */
  undo: () => boolean;
  redo: () => boolean;
}

function VideoPanelInner({
  source,
  videoRef,
  annotationCanvasRef,
  isPlaying,
  currentTime,
  duration,
  speed,
  isMuted,
  onTogglePlay,
  onSeek,
  onScrubStart,
  onScrubEnd,
  onSpeedChange,
  onToggleMute,
  pipOverlay,
  sideBySide = false,
  onVideoBoxHeight,
  drawingTool,
  drawColor,
  onSetDrawingTool,
  onSetDrawColor,
  onStrokeCommitted,
}: VideoPanelProps, ref: React.Ref<VideoPanelHandle>) {

  // Annotation canvas position — constrained to the video's CSS object-contain display area
  // so that canvas coords (0–1280, 0–720) map 1:1 to composite canvas coords. Without this,
  // strokes near the edges appear offset in the recording when the panel div's aspect ratio
  // differs from the video's (e.g., a 16:9 video in a 4:3 panel gets letterboxed differently
  // on screen vs. in the 1280×720 composite canvas).
  const panelAreaRef = useRef<HTMLDivElement>(null);
  /**
   * The area the box may occupy, and the box itself.
   *
   * Sized in JS rather than with `aspect-ratio`, because the CSS answer breaks
   * in exactly the case that matters here: with `aspect-ratio` plus both
   * `max-width` and `max-height`, whichever max clamps first makes that
   * dimension definite and the ratio is abandoned. Measuring the area and
   * computing `min(availW / srcW, availH / srcH)` is the same fit `object-fit:
   * contain` was already doing internally — so the video renders at exactly
   * the size it did before, and only the black around it goes away.
   */
  const videoAreaRef = useRef<HTMLDivElement>(null);
  const [videoBoxSize, setVideoBoxSize] = useState<{ w: number; h: number } | null>(null);
  const [annotationCanvasInset, setAnnotationCanvasInset] = useState<React.CSSProperties>({
    position: 'absolute', inset: 0, width: '100%', height: '100%',
  });
  const [panelAreaSize, setPanelAreaSize] = useState({ w: 0, h: 0 });

  const isCameraSource = source.isCameraSource === true;
  const isFreezeFrame = !isCameraSource && Boolean(source.segment) && source.segment!.startTime === source.segment!.endTime;

  /* Recomputes on area resize and on metadata, so growing the chrome below
     shrinks the box rather than reintroducing bars. */
  useEffect(() => {
    if (!sideBySide) { setVideoBoxSize(null); return; }
    const area = videoAreaRef.current;
    const video = videoRef.current;
    if (!area) return;
    const fit = () => {
      const availW = area.clientWidth;
      const availH = area.clientHeight;
      if (!availW || !availH) return;
      const rot = source.rotation ?? 0;
      const swapped = rot === 90 || rot === 270;
      const vw0 = video?.videoWidth ?? 0;
      const vh0 = video?.videoHeight ?? 0;
      const srcW = swapped ? vh0 : vw0;
      const srcH = swapped ? vw0 : vh0;
      // No metadata yet: fill the area, which is what it did before.
      if (!srcW || !srcH) { setVideoBoxSize({ w: availW, h: availH }); onVideoBoxHeight?.(availH); return; }
      const scale = Math.min(availW / srcW, availH / srcH);
      const box = { w: Math.floor(srcW * scale), h: Math.floor(srcH * scale) };
      setVideoBoxSize(box);
      onVideoBoxHeight?.(box.h);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(area);
    video?.addEventListener('loadedmetadata', fit);
    return () => { ro.disconnect(); video?.removeEventListener('loadedmetadata', fit); };
  // videoRef is a stable ref
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideBySide, source.videoUrl, source.rotation, isCameraSource]);

  useEffect(() => {
    // Camera source fills the panel div with no letterboxing — no adjustment needed.
    if (isCameraSource) return;
    const video = videoRef.current;
    const panel = panelAreaRef.current;
    if (!video || !panel) return;

    const updateCanvasPosition = () => {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const panelW = panel.clientWidth;
      const panelH = panel.clientHeight;
      if (!panelW || !panelH) return;
      setPanelAreaSize({ w: panelW, h: panelH });
      if (!vw || !vh) return;
      // For 90/270° rotated sources the visual aspect ratio is flipped — swap dims so
      // the overlay canvas covers the actual letterboxed video area, not the full panel.
      const rotation = source.rotation ?? 0;
      const isSwapped = rotation === 90 || rotation === 270;
      const effectiveVW = isSwapped ? vh : vw;
      const effectiveVH = isSwapped ? vw : vh;
      const scale = Math.min(panelW / effectiveVW, panelH / effectiveVH);
      const vidW = effectiveVW * scale;
      const vidH = effectiveVH * scale;
      setAnnotationCanvasInset({
        position: 'absolute',
        left: `${(panelW - vidW) / 2}px`,
        top: `${(panelH - vidH) / 2}px`,
        width: `${vidW}px`,
        height: `${vidH}px`,
      });
    };

    video.addEventListener('loadedmetadata', updateCanvasPosition);
    if (video.readyState >= 1) updateCanvasPosition();
    const ro = new ResizeObserver(updateCanvasPosition);
    ro.observe(panel);
    return () => {
      video.removeEventListener('loadedmetadata', updateCanvasPosition);
      ro.disconnect();
    };
  // videoRef and panelAreaRef are stable refs; re-run when the video source or camera flag changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.videoUrl, isCameraSource, source.rotation]);

  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // All annotations for this panel, each tied to a video timeline position.
  // An annotation is visible while currentTime ∈ [timestamp, timestamp + ANNOTATION_DURATION).
  const storedAnnotationsRef = useRef<StoredAnnotation[]>([]);
  const annotationTimestampRef = useRef<number>(0);

  /**
   * Undo and redo.
   *
   * There is no stroke list to walk back through: `handlePointerUp` snapshots
   * the whole canvas as ImageData and the snapshot replaces whatever was at
   * that timestamp. So the history is snapshot-based too. Each entry is the
   * annotation as it stood *before* one stroke, which `handlePointerDown`
   * already has to hand.
   *
   * Capped because a snapshot is PANEL_W x PANEL_H x 4 bytes: at 1280x720
   * that is 3.7MB per step, so ten steps is a ~37MB ceiling per panel. Raising
   * the cap is this one constant, at 3.7MB a step.
   */
  const undoStackRef = useRef<HistoryEntry[]>([]);
  const redoStackRef = useRef<HistoryEntry[]>([]);
  const [undoDepth, setUndoDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);
  /** The annotation as it stood before the stroke currently being drawn. */
  const strokeBaseRef = useRef<ImageData | null>(null);

  // Annotation tools are only available when this panel's video is paused
  /* Always. Drawing on a running video pauses it rather than being refused,
     so there is no state in which the pen is unavailable. */
  const canAnnotate = true;

  /* The tool used to be cleared when this panel started playing. It cannot be
     now — it is shared, and clearing it would disarm the pen on the paused
     panel because the other one is playing. The guard moved to the canvas:
     handlePointerDown refuses a stroke this panel cannot keep. */

  // Show or hide the active annotation based on the current video time
  useEffect(() => {
    if (isDrawingRef.current) return; // don't interfere while actively drawing
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const active = findActiveAnnotation(storedAnnotationsRef.current, currentTime);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (active) ctx.putImageData(active.imageData, 0, 0);
  // annotationCanvasRef and storedAnnotationsRef are stable refs — intentionally omitted from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime]);

  // Reset annotation state when video source changes (e.g., modal reopens)
  useEffect(() => {
    storedAnnotationsRef.current = [];
    annotationTimestampRef.current = 0;
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoDepth(0);
    setRedoDepth(0);
    const canvas = annotationCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.videoUrl]);

  const getCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = annotationCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const applyToolSettings = (ctx: CanvasRenderingContext2D, tool: DrawingTool) => {
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = ERASER_SIZE;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = COLOR_HEX[drawColor];
      ctx.fillStyle = COLOR_HEX[drawColor];
      ctx.lineWidth = BRUSH_SIZE;
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (drawingTool === 'none') return;
    e.preventDefault();
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);

    /* Drawing on a running video pauses it and keeps the stroke, rather than
       refusing it. Only this panel pauses; its partner is a different element
       and is not touched.

       `pause()` takes effect synchronously — it sets `paused` and stops the
       clock before returning, and only the `pause` *event* is deferred — so
       the timestamp read on the next line is the frame the coach is looking
       at. Nothing here waits on an event, so there is no window in which the
       pointer-down could be dropped: the handler runs straight on and captures
       the pointer above, before any of this. */
    const videoEl = videoRef.current;
    if (videoEl && !videoEl.paused) videoEl.pause();

    /* The video's own clock, not the `currentTime` prop. The prop is React
       state fed by timeupdate, which fires about four times a second, so at
       1x it can be up to ~250ms stale — a quarter second of play past the
       frame on screen. Reading the element is exact at any speed. */
    const frameTime = videoEl ? videoEl.currentTime : currentTime;

    // Set up canvas for this drawing session
    const canvas = annotationCanvasRef.current;
    const ctx2d = canvas?.getContext('2d');
    const active = findActiveAnnotation(storedAnnotationsRef.current, frameTime);

    // What undo will step back to, captured before the stroke touches anything.
    strokeBaseRef.current = active ? active.imageData : null;

    if (active) {
      // In range of an existing annotation — restore it as a base and accumulate strokes
      annotationTimestampRef.current = active.timestamp;
      if (ctx2d && canvas) {
        ctx2d.clearRect(0, 0, canvas.width, canvas.height);
        ctx2d.putImageData(active.imageData, 0, 0);
      }
    } else {
      // New annotation at the frame that was on screen when the stroke began
      annotationTimestampRef.current = frameTime;
      if (ctx2d && canvas) ctx2d.clearRect(0, 0, canvas.width, canvas.height);
    }

    isDrawingRef.current = true;
    const pt = getCanvasPoint(e);
    lastPointRef.current = pt;
    const ctx = annotationCanvasRef.current?.getContext('2d');
    if (!ctx) return;
    applyToolSettings(ctx, drawingTool);
    ctx.beginPath();
    const radius = drawingTool === 'eraser' ? ERASER_SIZE / 2 : BRUSH_SIZE / 2;
    ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
    ctx.fill();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || drawingTool === 'none') return;
    e.preventDefault();
    const pt = getCanvasPoint(e);
    const ctx = annotationCanvasRef.current?.getContext('2d');
    if (!ctx || !lastPointRef.current) return;
    applyToolSettings(ctx, drawingTool);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPointRef.current = pt;
  };

  const handlePointerUp = (_e: React.PointerEvent<HTMLCanvasElement>) => {
    const wasDrawing = isDrawingRef.current;
    isDrawingRef.current = false;
    lastPointRef.current = null;
    const canvas = annotationCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) ctx.globalCompositeOperation = 'source-over';
    // Save snapshot — replace the annotation at this timestamp if it exists, otherwise append
    if (wasDrawing && canvas && ctx) {
      const snap: StoredAnnotation = {
        timestamp: annotationTimestampRef.current,
        imageData: ctx.getImageData(0, 0, canvas.width, canvas.height),
      };
      undoStackRef.current = [
        ...undoStackRef.current.slice(-(MAX_UNDO_STEPS - 1)),
        { timestamp: snap.timestamp, imageData: strokeBaseRef.current },
      ];
      redoStackRef.current = [];
      setUndoDepth(undoStackRef.current.length);
      setRedoDepth(0);
      storedAnnotationsRef.current = [
        ...storedAnnotationsRef.current.filter((a) => a.timestamp !== snap.timestamp),
        snap,
      ];
    }
    strokeBaseRef.current = null;
    if (wasDrawing) onStrokeCommitted?.();
  };

  /** Repaint whatever is in range at the current time, the same rule the currentTime effect uses. */
  const repaintForCurrentTime = () => {
    const canvas = annotationCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const active = findActiveAnnotation(storedAnnotationsRef.current, currentTime);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (active) ctx.putImageData(active.imageData, 0, 0);
  };

  const setStoredAt = (timestamp: number, img: ImageData | null) => {
    const rest = storedAnnotationsRef.current.filter((a) => a.timestamp !== timestamp);
    storedAnnotationsRef.current = img ? [...rest, { timestamp, imageData: img }] : rest;
  };

  const storedAt = (timestamp: number) =>
    storedAnnotationsRef.current.find((a) => a.timestamp === timestamp)?.imageData ?? null;

  /** Step one stroke back. The entry carries its own timestamp, so undoing works
      after a scrub as well as immediately after drawing. */
  const handleUndo = (): boolean => {
    const entry = undoStackRef.current[undoStackRef.current.length - 1];
    if (!entry) return false;
    undoStackRef.current = undoStackRef.current.slice(0, -1);
    redoStackRef.current = [
      ...redoStackRef.current,
      { timestamp: entry.timestamp, imageData: storedAt(entry.timestamp) },
    ];
    setStoredAt(entry.timestamp, entry.imageData);
    annotationTimestampRef.current = entry.timestamp;
    repaintForCurrentTime();
    setUndoDepth(undoStackRef.current.length);
    setRedoDepth(redoStackRef.current.length);
    return true;
  };

  const handleRedo = (): boolean => {
    const entry = redoStackRef.current[redoStackRef.current.length - 1];
    if (!entry) return false;
    redoStackRef.current = redoStackRef.current.slice(0, -1);
    undoStackRef.current = [
      ...undoStackRef.current.slice(-(MAX_UNDO_STEPS - 1)),
      { timestamp: entry.timestamp, imageData: storedAt(entry.timestamp) },
    ];
    setStoredAt(entry.timestamp, entry.imageData);
    annotationTimestampRef.current = entry.timestamp;
    repaintForCurrentTime();
    setUndoDepth(undoStackRef.current.length);
    setRedoDepth(redoStackRef.current.length);
    return true;
  };

  /* Only undo and redo are exposed. The snapshots stay in this panel's refs —
     nothing about how they are taken changes. */
  useImperativeHandle(ref, () => ({ undo: handleUndo, redo: handleRedo }));

  const segStart = source.segment?.startTime ?? 0;
  const segEnd = segmentEndOf(source.segment?.endTime ?? null, duration);
  // When camera source or freeze frame, there are no video controls to show
  const showVideoControls = !isCameraSource && !isFreezeFrame;

  /* Speed and mute belong to a panel, not to the shared tools. Single source
     keeps them in the toolbar row where they have always been; side by side
     moves them to the end of this panel's own transport, because that row is
     no longer per-panel. */
  /* The transport. Rendered here for a single source; side by side the
     parent renders both, below the shared toolbar, so the drawing tools
     sit nearer the video than the playback controls do. */
  const canvasCursor =
    drawingTool === 'none' ? 'default' : drawingTool === 'eraser' ? 'cell' : 'crosshair';

  // canAnnotate for blank/freeze-frame: always allowed (no playing video)
  const canAnnotateEffective = isCameraSource || isFreezeFrame ? true : canAnnotate;

  return (
    /* gap-4 (16) between the title, the video and the transport when paired.
       It was gap-1.5 plus the toolbar's own 4px margins and the container's
       padding, which stacked into roughly 72 above and below the picture. */
    <div className={`flex flex-col min-w-0 flex-1 min-h-0 ${sideBySide ? 'gap-4' : 'gap-1.5'}`}>
      {/* The name of what is being recorded, not a debug label. Uppercase and
          letter-spacing were what made it read as one; at full strength and in
          sentence case it reads as a title. 13/16 keeps the row at the 16 it
          was, so nothing is taken from the video. */}
      {!source.label && sideBySide && <div className={`${sideBySide ? 'h-6' : 'h-4'} flex-shrink-0`} aria-hidden="true" />}
      {source.label && (
        /* Centred over its own video and larger when paired, so two clips of
           the same stroke are told apart without reading carefully. Paid for
           by the letterbox the box no longer draws. */
        <div className={`flex items-center gap-1.5 px-1 min-w-0 flex-shrink-0 ${sideBySide ? 'justify-center' : ''}`}>
          <span className={`font-semibold text-[var(--editor-text)] truncate ${sideBySide ? 'text-[15px] leading-6' : 'text-[13px] leading-4'}`}>
            {source.label}
          </span>
          {isFreezeFrame && (
            <span className={`text-[var(--editor-text-muted)] flex-shrink-0 ${sideBySide ? 'text-xs leading-6' : 'text-[11px] leading-4'}`}>
              (freeze frame)
            </span>
          )}
        </div>
      )}

      {/* Video / blank / freeze-frame panel + annotation canvas.
          The outer div is the area; the inner one is the box, sized to the
          source so the panel has no letterbox of its own. */}
      <div ref={videoAreaRef} className="flex-1 min-h-0 w-full flex items-center justify-center">
      <div
        ref={panelAreaRef}
        className={`relative bg-black rounded-lg overflow-hidden ${videoBoxSize ? '' : 'w-full h-full'}`}
        style={videoBoxSize ? { width: videoBoxSize.w, height: videoBoxSize.h } : undefined}
      >
        {/* Camera source: live feed via srcObject set by parent; Normal source: src URL */}
        {isCameraSource ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-contain"
            style={{ transform: 'scaleX(-1)' }}
          />
        ) : (
          <video
            ref={videoRef}
            src={source.videoUrl}
            className="w-full h-full object-contain"
            crossOrigin="anonymous"
            playsInline
            preload="metadata"
            style={source.rotation ? (() => {
              const needsScale = source.rotation === 90 || source.rotation === 270;
              const scale = needsScale && panelAreaSize.w > 0 && panelAreaSize.h > 0
                ? Math.min(panelAreaSize.w, panelAreaSize.h) / Math.max(panelAreaSize.w, panelAreaSize.h)
                : 1;
              return { transform: `rotate(${source.rotation}deg)${scale !== 1 ? ` scale(${scale})` : ''}` };
            })() : undefined}
          />
        )}
        <canvas
          ref={annotationCanvasRef}
          width={PANEL_W}
          height={PANEL_H}
          style={{ ...annotationCanvasInset, cursor: canvasCursor, touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        {/* Timestamp watermark — not shown for camera source */}
        {!isCameraSource && (
          <div
            /* Same padding and corner as a speed pill (px-2.5, radius 8), so
               the two chips read as one family. Tenths stay: they are what an
               annotation is keyed to. */
            className="absolute px-2.5 py-0.5 rounded-[8px] text-white text-[11px] font-mono pointer-events-none select-none"
            style={{ top: 12, left: 12, backgroundColor: 'rgba(0,0,0,0.6)', textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}
          >
            {formatTimeDetailed(currentTime)}
          </div>
        )}
        {pipOverlay}
      </div>
      </div>

      {/* Single source keeps its own toolbar exactly as it was: one panel, one
          set of tools, nothing to share. Side by side renders the shared row in
          the parent instead. */}
      {!sideBySide && (
        <DrawToolbar
          drawingTool={drawingTool}
          setDrawingTool={onSetDrawingTool}
          drawColor={drawColor}
          setDrawColor={onSetDrawColor}
          canAnnotate={canAnnotateEffective}
          undoDepth={undoDepth}
          redoDepth={redoDepth}
          onUndo={handleUndo}
          onRedo={handleRedo}
        >
          <PanelUtilities showVideoControls={showVideoControls} speed={speed} onSpeedChange={onSpeedChange} isMuted={isMuted} onToggleMute={onToggleMute} />
        </DrawToolbar>
      )}

      {/* Back inside the column, directly under this panel's own video. */}
      {(
        <PanelTransport
          showVideoControls={showVideoControls} sideBySide={sideBySide}
          onScrubStart={onScrubStart} onScrubEnd={onScrubEnd}
          isPlaying={isPlaying} onTogglePlay={onTogglePlay}
          segStart={segStart} segEnd={segEnd} currentTime={currentTime} onSeek={onSeek}
          speed={speed} onSpeedChange={onSpeedChange}
          isMuted={isMuted} onToggleMute={onToggleMute}
        />
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ClipEditorModal({
  isOpen,
  onClose,
  onSave,
  onSaveAndContinue,
  primarySource,
  secondarySource,
  embedded = false,
  onAddSecondarySource,
  initiallyRecording = false,
}: ClipEditorModalProps) {
  const hasTwoSources = !!secondarySource;

  // Recording state
  const [isRecording, setIsRecording] = useState(initiallyRecording);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const rafIdRef = useRef<number | null>(null);
  const compositeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const canceledRef = useRef(false);
  /** true when the user chose Abort — stop without saving, keep modal open */
  const abortRef = useRef(false);
  /** 'close' → call onSave + onClose; 'continue' → call onSaveAndContinue, keep modal open */
  const saveModeRef = useRef<'close' | 'continue'>('close');
  const audioContextRef = useRef<AudioContext | null>(null);
  const captureStreamsRef = useRef<MediaStream[]>([]);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  const recordingStartMsRef = useRef<number>(0);

  // Camera source stream (when primarySource.isCameraSource — managed by its own useEffect)
  const cameraStreamRef = useRef<MediaStream | null>(null);
  // Secondary camera stream (when secondarySource.isCameraSource — managed by its own useEffect)
  const secondaryCameraStreamRef = useRef<MediaStream | null>(null);

  // PiP camera overlay (picture-in-picture circle on the primary panel during recording)
  const pipCameraVideoRef = useRef<HTMLVideoElement | null>(null);
  // Off-DOM video element used exclusively for canvas drawImage — decoupled from layout
  // so that DOM reflows (e.g. the recording UI appearing) cannot corrupt the captured frame.
  const pipCaptureVideoRef = useRef<HTMLVideoElement | null>(null);
  const pipCameraStreamRef = useRef<MediaStream | null>(null);
  const [pipEnabled, setPipEnabled] = useState(true);
  const pipEnabledRef = useRef(true);
  const panelsContainerRef = useRef<HTMLDivElement>(null);

  /**
   * The displayed video's content box, in pixels relative to the panels
   * container. This is the space the bubble now lives in.
   *
   * Derived with the same contain maths the composite canvas uses, seeded by a
   * measured element box. Pure derivation is not cleaner here: the element's
   * own size depends on the flex layout, the label row and the container's
   * padding, so the DOM has to be measured either way. Only the contain step
   * is computed.
   *
   * Side by side is deliberately the whole container: that canvas is still a
   * fixed 2560x720 with each source contained inside its half, so the bars are
   * part of the canvas there and the container is the matching space.
   */
  const [dividerHeight, setDividerHeight] = useState(0);

  /* ── Shared drawing tools ────────────────────────────────────────────────
     The tool and the colour live here so both panels answer to one control.
     The ImageData snapshots do not move: each panel keeps its own, and this
     holds nothing but the order strokes were committed in, so one Undo can
     step back through both panels most-recent-first. */
  const [drawingTool, setDrawingTool] = useState<DrawingTool>('none');
  const [drawColor, setDrawColor] = useState<DrawColor>('red');
  const panel1Ref = useRef<VideoPanelHandle>(null);
  const panel2Ref = useRef<VideoPanelHandle>(null);
  const strokeOrderRef = useRef<(1 | 2)[]>([]);
  const redoOrderRef = useRef<(1 | 2)[]>([]);
  const [historyDepth, setHistoryDepth] = useState({ undo: 0, redo: 0 });

  const noteStroke = useCallback((panel: 1 | 2) => {
    /* Capped to match each panel's own MAX_UNDO_STEPS so the list cannot grow
       far past what the panels can actually step back through. */
    strokeOrderRef.current = [...strokeOrderRef.current.slice(-(MAX_UNDO_STEPS * 2 - 1)), panel];
    redoOrderRef.current = [];
    setHistoryDepth({ undo: strokeOrderRef.current.length, redo: 0 });
  }, []);

  const handleSharedUndo = useCallback(() => {
    /* A panel's own stack is capped at 10, so an entry here can outlive the
       snapshot it refers to. undo() reports that, and we walk on to the next
       most recent stroke rather than doing nothing. */
    while (strokeOrderRef.current.length > 0) {
      const panel = strokeOrderRef.current[strokeOrderRef.current.length - 1];
      strokeOrderRef.current = strokeOrderRef.current.slice(0, -1);
      const did = panel === 1 ? panel1Ref.current?.undo() : panel2Ref.current?.undo();
      if (did) {
        redoOrderRef.current = [...redoOrderRef.current, panel];
        break;
      }
    }
    setHistoryDepth({ undo: strokeOrderRef.current.length, redo: redoOrderRef.current.length });
  }, []);

  const handleSharedRedo = useCallback(() => {
    while (redoOrderRef.current.length > 0) {
      const panel = redoOrderRef.current[redoOrderRef.current.length - 1];
      redoOrderRef.current = redoOrderRef.current.slice(0, -1);
      const did = panel === 1 ? panel1Ref.current?.redo() : panel2Ref.current?.redo();
      if (did) {
        strokeOrderRef.current = [...strokeOrderRef.current, panel];
        break;
      }
    }
    setHistoryDepth({ undo: strokeOrderRef.current.length, redo: redoOrderRef.current.length });
  }, []);

  const [videoBox, setVideoBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  // Panel 1 refs + state
  const video1Ref = useRef<HTMLVideoElement>(null);
  const annotationCanvas1Ref = useRef<HTMLCanvasElement>(null);
  const [p1IsPlaying, setP1IsPlaying] = useState(false);
  const [p1CurrentTime, setP1CurrentTime] = useState(primarySource.segment?.startTime ?? 0);
  const [p1Duration, setP1Duration] = useState(0);
  const [p1Speed, setP1Speed] = useState<Speed>(1);
  const p1SpeedRef = useRef<Speed>(1);
  const [p1Muted, setP1Muted] = useState(true);
  const p1MutedRef = useRef(true);
  // Ref so drawFrame (rAF closure) can always read the latest current time
  const p1CurrentTimeRef = useRef<number>(primarySource.segment?.startTime ?? 0);
  // Stable ref to the segment start — readable in handleTogglePlay1 ([] deps callback)
  const p1SegStartRef = useRef<number>(primarySource.segment?.startTime ?? 0);
  // Infinity when no segment (currentTime >= Infinity is always false — no restart logic)
  /* null means "no segment": the end of the range is the end of the video,
     which is only known once metadata has loaded. Resolved at call time by
     segmentEndOf() rather than frozen to a number here. */
  const p1SegEndRef = useRef<number | null>(primarySource.segment?.endTime ?? null);
  // Latest scrub position queued while a seek is already in progress
  const p1PendingSeekRef = useRef<number | null>(null);
  /* What the coach last asked this panel to do, which is not the same question
     as `video.paused`.

     `play()` flips `paused` to false synchronously, long before any data
     arrives, so on a cold video a second click read "already playing" and
     called pause() — cancelling the load it was trying to start. Alternating
     clicks cancelled each other, which is what "it took ten clicks" was.

     Intent lives here; reality lives on the element. Reality always wins: the
     `pause` listener below clears this on every pause whatever caused it, so
     the two cannot drift. */
  const p1WantsPlayRef = useRef(false);

  // Panel 2 refs + state
  const video2Ref = useRef<HTMLVideoElement>(null);
  const annotationCanvas2Ref = useRef<HTMLCanvasElement>(null);
  const [p2IsPlaying, setP2IsPlaying] = useState(false);
  const [p2CurrentTime, setP2CurrentTime] = useState(secondarySource?.segment?.startTime ?? 0);
  const [p2Duration, setP2Duration] = useState(0);
  const [p2Speed, setP2Speed] = useState<Speed>(1);
  const p2SpeedRef = useRef<Speed>(1);
  const [p2Muted, setP2Muted] = useState(true);
  const p2MutedRef = useRef(true);
  const p2CurrentTimeRef = useRef<number>(secondarySource?.segment?.startTime ?? 0);
  // Stable ref to the segment start — readable in handleTogglePlay2 ([] deps callback)
  const p2SegStartRef = useRef<number>(secondarySource?.segment?.startTime ?? 0);
  const p2SegEndRef = useRef<number | null>(secondarySource?.segment?.endTime ?? null);
  const p2PendingSeekRef = useRef<number | null>(null);
  // See the note on p1WantsPlayRef.
  const p2WantsPlayRef = useRef(false);

  /* One control, one answer. Enabled when either panel could keep a stroke;
     a panel that could not simply refuses the pointer. */
  const p1CanAnnotate = primarySource.isCameraSource === true
    || (!!primarySource.segment && primarySource.segment.startTime === primarySource.segment.endTime)
    || !p1IsPlaying;
  const p2CanAnnotate = !secondarySource ? false : (secondarySource.isCameraSource === true
    || (!!secondarySource.segment && secondarySource.segment.startTime === secondarySource.segment.endTime)
    || !p2IsPlaying);
  const sharedCanAnnotate = p1CanAnnotate || p2CanAnnotate;

  // Reset state when modal opens / source changes
  const initialRenderRef = useRef(true);
  useEffect(() => {
    if (!isOpen) return;
    // Skip recording reset on first render if initiallyRecording is true
    if (initialRenderRef.current && initiallyRecording) {
      initialRenderRef.current = false;
    } else if (mediaRecorderRef.current === null) {
      // Don't reset isRecording if the recorder is mid-finalization (onstop hasn't fired yet).
      // primarySource can change synchronously from onSaveAndContinue inside onstop — if
      // we reset isRecording here before onstop finishes assembling the blob, and the user
      // immediately starts a new recording, chunksRef gets cleared before the old blob is built.
      setIsRecording(false);
    }
    canceledRef.current = false;
  }, [isOpen, primarySource, secondarySource]);

  /* Per-panel reset, split in two.
     One effect used to reset BOTH panels on `[isOpen, primarySource,
     secondarySource]`, which made adding a comparison source mid-session
     rewind panel 1, drop its speed ref to 1 and re-mute it — while the element
     itself kept playing at whatever it was, so the refs and the video
     disagreed from then on. Each panel now resets only when its own source
     changes. */
  useEffect(() => {
    if (!isOpen) return;
    const p1Start = primarySource.segment?.startTime ?? 0;
    setP1CurrentTime(p1Start);
    p1CurrentTimeRef.current = p1Start;
    p1SegStartRef.current = p1Start;
    p1SegEndRef.current = primarySource.segment?.endTime ?? null;
    p1PendingSeekRef.current = null;
    /* A new src runs the element's load algorithm, which sets `paused` to true
       WITHOUT firing a `pause` event — so the listener that reconciles this ref
       never sees it. Cleared here with the rest of this panel's state. */
    p1WantsPlayRef.current = false;
    setP1Speed(1);
    p1SpeedRef.current = 1;
    setP1Muted(true);
    p1MutedRef.current = true;
  }, [isOpen, primarySource]);

  useEffect(() => {
    if (!isOpen) return;
    const p2Start = secondarySource?.segment?.startTime ?? 0;
    setP2CurrentTime(p2Start);
    p2CurrentTimeRef.current = p2Start;
    p2SegStartRef.current = p2Start;
    p2SegEndRef.current = secondarySource?.segment?.endTime ?? null;
    p2PendingSeekRef.current = null;
    // See the note in panel 1's reset.
    p2WantsPlayRef.current = false;
    setP2Speed(1);
    p2SpeedRef.current = 1;
    setP2Muted(true);
    p2MutedRef.current = true;
  }, [isOpen, secondarySource]);

  // Camera stream acquisition — runs when modal opens with a camera primary source
  useEffect(() => {
    if (!isOpen || !primarySource.isCameraSource) return;
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        cameraStreamRef.current = stream;
        if (video1Ref.current) video1Ref.current.srcObject = stream;
      })
      .catch((err) => console.error('ClipEditorModal: camera access failed', err));
    return () => {
      cancelled = true;
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
      if (video1Ref.current) video1Ref.current.srcObject = null;
    };
  }, [isOpen, primarySource.isCameraSource]);

  // Secondary camera stream acquisition — runs when modal opens with a camera secondary source
  useEffect(() => {
    if (!isOpen || !secondarySource?.isCameraSource) return;
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        secondaryCameraStreamRef.current = stream;
        if (video2Ref.current) {
          video2Ref.current.srcObject = stream;
          video2Ref.current.play().catch(() => {});
        }
      })
      .catch((err) => console.error('ClipEditorModal: secondary camera access failed', err));
    return () => {
      cancelled = true;
      secondaryCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      secondaryCameraStreamRef.current = null;
      if (video2Ref.current) video2Ref.current.srcObject = null;
    };
  }, [isOpen, secondarySource?.isCameraSource]);

  // PiP camera stream — acquired/released when the PiP checkbox is toggled
  useEffect(() => {
    pipEnabledRef.current = pipEnabled;
    if (!pipEnabled) {
      pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      pipCameraStreamRef.current = null;
      pipCaptureVideoRef.current = null;
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: true })
      .then((stream) => {
        pipCameraStreamRef.current = stream;
        // Off-DOM capture element — never in the layout, so layout reflows (e.g. the
        // recording UI appearing) cannot corrupt the frame returned by drawImage.
        const captureEl = document.createElement('video');
        captureEl.srcObject = stream;
        captureEl.autoplay = true;
        captureEl.muted = true;
        captureEl.playsInline = true;
        captureEl.play().catch(() => {});
        pipCaptureVideoRef.current = captureEl;
        // pipCameraVideoRef may or may not be mounted yet — the callback ref handles late binding
        if (pipCameraVideoRef.current) {
          pipCameraVideoRef.current.srcObject = stream;
          pipCameraVideoRef.current.play().catch(() => {});
        }
      })
      .catch((err) => {
        console.error('ClipEditorModal: PiP camera access failed', err);
        setPipEnabled(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipEnabled]);

  // Callback ref for the PiP video element — sets srcObject immediately on mount
  // so the live feed starts even if the stream arrived before the element rendered.
  const setPipVideoRef = useCallback((el: HTMLVideoElement | null) => {
    pipCameraVideoRef.current = el;
    if (el && pipCameraStreamRef.current) {
      el.srcObject = pipCameraStreamRef.current;
      el.play().catch(() => {});
    }
  }, []);

  // Panel 1 video events
  useEffect(() => {
    if (primarySource.isCameraSource) return; // camera feed has no seekable timeline
    const video = video1Ref.current;
    if (!video || !isOpen) return;

    const handleLoaded = () => {
      const t = primarySource.segment?.startTime ?? 0;
      setP1Duration(video.duration);
      p1CurrentTimeRef.current = t;
      setP1CurrentTime(t);
      // Apply current speed/mute state immediately — user may have changed them before
      // the video element was ready.
      applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
      // Pre-warm: seek to segment start so the first play is instant (no cold range
      // request on click).  The seek→timeupdate→seek infinite loop that previously
      // caused CPU spin is now prevented by the `if (video.seeking) return` guard
      // in handleTimeUpdate, so this seek fires exactly once.
      if (t > 0) video.currentTime = t;
    };

    const handleTimeUpdate = () => {
      // Bail while a seek is in progress — prevents seek → timeupdate → seek loop
      if (video.seeking) return;
      const seg = primarySource.segment;
      if (seg) {
        // Use SEGMENT_START_EPSILON here: after a seek to seg.startTime, floating-point
        // representation can make video.currentTime land a tiny fraction below the target.
        // Without the epsilon, this fires another seek → seeked → timeupdate → seek loop
        // which keeps the CPU busy and also aborts any pending play() promise.
        if (video.currentTime < seg.startTime - SEGMENT_START_EPSILON) {
          video.currentTime = seg.startTime;
          return;
        }
        if (video.currentTime >= seg.endTime) {
          video.pause();
          video.currentTime = seg.endTime;
          setP1IsPlaying(false);
          p1CurrentTimeRef.current = seg.endTime;
          setP1CurrentTime(seg.endTime);
          /* Panel 2 is not touched. The panels are independent: one reaching
             the end of its segment says nothing about the other, which may be
             a different video of a different length that the coach is still
             watching. This used to call video2Ref.current?.pause().

             It read as symmetric with the matching line in panel 2's handler,
             and was not. Both sit inside `if (seg)`, and a comparison source
             picked from the drill library carries no segment — so panel 2
             could never pause panel 1, while panel 1 paused panel 2 every
             time. Worse, `timeupdate` fires on a currentTime assignment and
             not only during playback, so once panel 1 was parked at its
             segment end any later seek on it re-entered this branch and killed
             panel 2 again. */
          return;
        }
      }
      p1CurrentTimeRef.current = video.currentTime;
      setP1CurrentTime(video.currentTime);
    };

    const handlePlay = () => {
      setP1IsPlaying(true);
      // Re-apply speed/mute after play starts — some browsers reset playbackRate
      // during play() or when it was set before the video was in a playable state.
      applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
    };
    /* The one place intent is reconciled with reality. Every way this panel can
       stop without a click goes through a real `pause` event — segment end,
       auto-pause on draw, recording stop/cancel/unmount/close, a media error,
       an OS or media-key pause — so clearing the intent here closes all of
       them at once. Without this the ref would still claim the panel wants to
       play and the next click would pause an already-paused element, which is
       the same dead click in a different costume. */
    const handlePause = () => {
      p1WantsPlayRef.current = false;
      setP1IsPlaying(false);
    };

    // On seek complete: apply any scrub position queued while this seek was in progress.
    // No muted/speed restoration here — those are set atomically via applyVideoState
    // at the call sites (handleTogglePlay1, handleSpeedChange1, handleLoaded) so there
    // is no race between seeking events and the play() call.
    const handleSeeked = () => {
      const pendingT = p1PendingSeekRef.current;
      if (pendingT !== null) {
        p1PendingSeekRef.current = null;
        video.currentTime = pendingT;
      }
    };

    video.addEventListener('loadedmetadata', handleLoaded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);

    // If the video is already loaded (e.g., cached from a previous open),
    // loadedmetadata won't fire again — initialise manually.
    if (video.readyState >= 1) handleLoaded();

    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [isOpen, primarySource]);

  // Panel 2 video events
  useEffect(() => {
    if (!secondarySource) return;
    const video = video2Ref.current;
    if (!video || !isOpen) return;

    const handleLoaded = () => {
      const t = secondarySource.segment?.startTime ?? 0;
      setP2Duration(video.duration);
      p2CurrentTimeRef.current = t;
      setP2CurrentTime(t);
      applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
      if (t > 0) video.currentTime = t;
    };

    const handleTimeUpdate = () => {
      // Bail while a seek is in progress — prevents seek → timeupdate → seek loop
      if (video.seeking) return;
      const seg = secondarySource.segment;
      if (seg) {
        if (video.currentTime < seg.startTime - SEGMENT_START_EPSILON) {
          video.currentTime = seg.startTime;
          return;
        }
        if (video.currentTime >= seg.endTime) {
          video.pause();
          video.currentTime = seg.endTime;
          setP2IsPlaying(false);
          p2CurrentTimeRef.current = seg.endTime;
          setP2CurrentTime(seg.endTime);
          // Panel 1 is not touched. See the matching note in panel 1's handler.
          return;
        }
      }
      p2CurrentTimeRef.current = video.currentTime;
      setP2CurrentTime(video.currentTime);
    };

    const handlePlay = () => {
      setP2IsPlaying(true);
      applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
    };
    // See the note in panel 1's handlePause.
    const handlePause = () => {
      p2WantsPlayRef.current = false;
      setP2IsPlaying(false);
    };

    const handleSeeked = () => {
      const pendingT = p2PendingSeekRef.current;
      if (pendingT !== null) {
        p2PendingSeekRef.current = null;
        video.currentTime = pendingT;
      }
    };

    video.addEventListener('loadedmetadata', handleLoaded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);

    // If the video is already loaded (e.g., cached from a previous open),
    // loadedmetadata won't fire again — initialise manually.
    if (video.readyState >= 1) handleLoaded();

    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [isOpen, secondarySource]);

  // Panel 1 handlers — each panel is fully independent
  const handleTogglePlay1 = useCallback(() => {
    const video = video1Ref.current;
    if (!video) return;
    /* Branch on intent, not on `video.paused`. While a cold video is still
       buffering `paused` is already false, so this used to pause it. */
    if (p1WantsPlayRef.current) {
      p1WantsPlayRef.current = false;
      video.pause();
      return;
    }
    p1WantsPlayRef.current = true;

    const segStart = p1SegStartRef.current;
    const segEnd = segmentEndOf(p1SegEndRef.current, video.duration);

    // Seek to segment start if we're at-or-before it, or at/past the end.
    // Using <= (not <) for the lower bound: when currentTime is exactly at segStart
    // the pre-warm seek may have just completed and floating-point state may not be
    // fully settled.  Re-seeking ensures a clean position and queues play() safely
    // while the seek is in flight.  The browser handles play-while-seeking natively.
    if (video.currentTime <= segStart || video.currentTime >= segEnd) {
      video.currentTime = segStart;
    }

    applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
    video.play().catch((err) => {
      /* A rejected play() leaves the element paused without firing `pause`, so
         this is the one stop the listener above cannot see. Clearing here keeps
         the next click a play rather than a no-op pause. */
      p1WantsPlayRef.current = false;
      console.warn('ClipEditorModal: panel 1 play() failed:', err);
    });
  }, []);

  const handleSeek1 = useCallback((t: number) => {
    p1CurrentTimeRef.current = t;
    setP1CurrentTime(t);
    const video = video1Ref.current;
    if (!video) return;
    if (video.seeking) {
      // Another seek in progress — queue this position; handleSeeked will apply it
      p1PendingSeekRef.current = t;
    } else {
      video.currentTime = t;
    }
  }, []);

  const handleSpeedChange1 = useCallback((s: Speed) => {
    p1SpeedRef.current = s;
    if (video1Ref.current) applyVideoState(video1Ref.current, s, p1MutedRef.current);
    setP1Speed(s);
  }, []);

  const handleToggleMute1 = useCallback(() => {
    const next = !p1MutedRef.current;
    p1MutedRef.current = next;
    setP1Muted(next);
    if (video1Ref.current) applyVideoState(video1Ref.current, p1SpeedRef.current, next);
  }, []);

  // Panel 2 handlers — each panel is fully independent
  const handleTogglePlay2 = useCallback(() => {
    const video = video2Ref.current;
    if (!video) return;
    // See the note in handleTogglePlay1.
    if (p2WantsPlayRef.current) {
      p2WantsPlayRef.current = false;
      video.pause();
      return;
    }
    p2WantsPlayRef.current = true;

    const segStart = p2SegStartRef.current;
    const segEnd = segmentEndOf(p2SegEndRef.current, video.duration);

    if (video.currentTime <= segStart || video.currentTime >= segEnd) {
      video.currentTime = segStart;
    }

    applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
    video.play().catch((err) => {
      // See the note in handleTogglePlay1.
      p2WantsPlayRef.current = false;
      console.warn('ClipEditorModal: panel 2 play() failed:', err);
    });
  }, []);

  /* Scrub-to-pause.
     Bespoke rather than the shared useScrubPause hook, and for one reason:
     these panels carry an explicit intent ref, and the hook only knows about
     the element. Pausing here fires the `pause` listener, which clears
     pWantsPlayRef — correctly, since reality won. On release we have to put
     the intent back as well as restart the element, or the panel would be
     playing while its ref said otherwise and the next click would call play()
     on a playing video instead of pausing it.

     So intent, not `!video.paused`, is what gets captured: it is the same
     question ("did the coach want this running?") asked of the right source. */
  const p1ScrubResumeRef = useRef(false);
  const p2ScrubResumeRef = useRef(false);

  const handleScrubStart1 = useCallback(() => {
    const video = video1Ref.current;
    if (!video) return;
    p1ScrubResumeRef.current = p1WantsPlayRef.current;
    if (p1WantsPlayRef.current) video.pause();
  }, []);

  const handleScrubEnd1 = useCallback(() => {
    if (!p1ScrubResumeRef.current) return;
    p1ScrubResumeRef.current = false;
    const video = video1Ref.current;
    if (!video) return;
    p1WantsPlayRef.current = true;
    applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
    video.play().catch(() => { p1WantsPlayRef.current = false; });
  }, []);

  const handleScrubStart2 = useCallback(() => {
    const video = video2Ref.current;
    if (!video) return;
    p2ScrubResumeRef.current = p2WantsPlayRef.current;
    if (p2WantsPlayRef.current) video.pause();
  }, []);

  const handleScrubEnd2 = useCallback(() => {
    if (!p2ScrubResumeRef.current) return;
    p2ScrubResumeRef.current = false;
    const video = video2Ref.current;
    if (!video) return;
    p2WantsPlayRef.current = true;
    applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
    video.play().catch(() => { p2WantsPlayRef.current = false; });
  }, []);

  const handleSeek2 = useCallback((t: number) => {
    p2CurrentTimeRef.current = t;
    setP2CurrentTime(t);
    const video = video2Ref.current;
    if (!video) return;
    if (video.seeking) {
      p2PendingSeekRef.current = t;
    } else {
      video.currentTime = t;
    }
  }, []);

  const handleSpeedChange2 = useCallback((s: Speed) => {
    p2SpeedRef.current = s;
    if (video2Ref.current) applyVideoState(video2Ref.current, s, p2MutedRef.current);
    setP2Speed(s);
  }, []);

  const handleToggleMute2 = useCallback(() => {
    const next = !p2MutedRef.current;
    p2MutedRef.current = next;
    setP2Muted(next);
    if (video2Ref.current) applyVideoState(video2Ref.current, p2SpeedRef.current, next);
  }, []);

  // Start recording
  const handleStartRecording = useCallback(async () => {
    // Guard on both isRecording state AND the ref — mediaRecorderRef stays non-null
    // until onstop fires, so this prevents starting a new recording while the previous
    // clip is still being finalized (which would clear chunksRef and corrupt the old blob).
    if (isRecording || mediaRecorderRef.current !== null) return;
    canceledRef.current = false;

    // Create / reset composite canvas
    if (!compositeCanvasRef.current) {
      compositeCanvasRef.current = document.createElement('canvas');
    }
    const composite = compositeCanvasRef.current;
    /**
     * Single source: the canvas takes the source's aspect, so containRect below
     * fills it exactly and no bars are drawn. Side by side keeps the existing
     * fixed 2560x720 for now; laying out two arbitrary aspects is its own
     * decision and is not made here.
     */
    const v1El = video1Ref.current;
    const p1Rot = primarySource.rotation ?? 0;
    const p1Swapped = p1Rot === 90 || p1Rot === 270;
    const srcW = p1Swapped ? (v1El?.videoHeight ?? 0) : (v1El?.videoWidth ?? 0);
    const srcH = p1Swapped ? (v1El?.videoWidth ?? 0) : (v1El?.videoHeight ?? 0);
    // A camera source is already drawn edge to edge, so it keeps the landscape frame.
    const single = primarySource.isCameraSource === true
      ? { w: PANEL_W, h: PANEL_H }
      : compositeSizeFor(srcW, srcH);
    const panelW = hasTwoSources ? PANEL_W : single.w;
    const panelH = hasTwoSources ? PANEL_H : single.h;

    composite.width = hasTwoSources ? PANEL_W * 2 : panelW;
    composite.height = panelH;
    const ctx = composite.getContext('2d')!;

    // Initialize canvas to black before loop starts
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, composite.width, composite.height);

    // captureStream(30) — Chrome's internal 30fps limiter keeps encoder load reasonable.
    // The internal capture timer stops when the track is stopped (done synchronously in
    // handleStopRecording via canvasStreamRef), so there is no lingering timer after recording.
    const canvasStream = composite.captureStream(30);
    canvasStreamRef.current = canvasStream;

    // rAF draw loop — for camera sources, video1Ref has srcObject (live feed) so drawing
    // is identical to a normal video source. Skip seeking check for camera (always ready).
    // Compute object-contain draw rect so portrait videos get letterboxed
    // rather than stretched to fill the panel.
    const containRect = (vw: number, vh: number, panelX: number) => {
      const scale = Math.min(panelW / vw, panelH / vh);
      const w = vw * scale;
      const h = vh * scale;
      return { x: panelX + (panelW - w) / 2, y: (panelH - h) / 2, w, h };
    };

    const p1IsCamera = primarySource.isCameraSource === true;
    const p1Rotation = primarySource.rotation ?? 0;
    const drawFrame = () => {
      const v1 = video1Ref.current;
      if (v1 && (p1IsCamera || !v1.seeking)) {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, panelW, panelH);
        if (v1.readyState >= 2) {
          if (p1IsCamera) {
            ctx.save();
            ctx.scale(-1, 1);
            ctx.drawImage(v1, -panelW, 0, panelW, panelH);
            ctx.restore();
          } else {
            const vw0 = v1.videoWidth || panelW;
            const vh0 = v1.videoHeight || panelH;
            // For 90/270 rotation the visual aspect ratio flips — swap dims for containRect
            const isSwapped = p1Rotation === 90 || p1Rotation === 270;
            const r = containRect(isSwapped ? vh0 : vw0, isSwapped ? vw0 : vh0, 0);
            if (p1Rotation !== 0) {
              ctx.save();
              ctx.translate(r.x + r.w / 2, r.y + r.h / 2);
              ctx.rotate((p1Rotation * Math.PI) / 180);
              // In rotated space, natural video dimensions fill a rect of size r.h × r.w
              ctx.drawImage(v1, -r.h / 2, -r.w / 2, r.h, r.w);
              ctx.restore();
            } else {
              ctx.drawImage(v1, r.x, r.y, r.w, r.h);
            }
          }
        }
        const ac1 = annotationCanvas1Ref.current;
        if (ac1) {
          if (p1IsCamera) {
            ctx.drawImage(ac1, 0, 0, panelW, panelH);
          } else {
            const vw0 = v1.videoWidth || panelW;
            const vh0 = v1.videoHeight || panelH;
            const isSwapped = p1Rotation === 90 || p1Rotation === 270;
            const ar = containRect(isSwapped ? vh0 : vw0, isSwapped ? vw0 : vh0, 0);
            ctx.drawImage(ac1, ar.x, ar.y, ar.w, ar.h);
          }
        }
      }

      if (hasTwoSources) {
        const v2 = video2Ref.current;
        const p2IsCamera = secondarySource?.isCameraSource === true;
        if (v2 && (p2IsCamera || !v2.seeking)) {
          ctx.fillStyle = '#000';
          ctx.fillRect(PANEL_W, 0, PANEL_W, PANEL_H);
          if (v2.readyState >= 2) {
            const vw = v2.videoWidth || PANEL_W;
            const vh = v2.videoHeight || PANEL_H;
            const r = containRect(vw, vh, PANEL_W);
            ctx.drawImage(v2, r.x, r.y, r.w, r.h);
          }
          const ac2 = annotationCanvas2Ref.current;
          if (ac2) {
            if (p2IsCamera) {
              ctx.drawImage(ac2, PANEL_W, 0, PANEL_W, PANEL_H);
            } else {
              const vw = v2.videoWidth || PANEL_W;
              const vh = v2.videoHeight || PANEL_H;
              const ar2 = containRect(vw, vh, PANEL_W);
              ctx.drawImage(ac2, ar2.x, ar2.y, ar2.w, ar2.h);
            }
          }
        }
      }

      // Draw timestamp watermarks — only for non-camera panels
      if (!p1IsCamera) {
        const v1Now = video1Ref.current;
        if (v1Now && !v1Now.seeking && v1Now.readyState >= 2) {
          ctx.save();
          const FONT_SIZE = 28;
          ctx.font = `bold ${FONT_SIZE}px monospace`;
          const text = formatTimeDetailed(p1CurrentTimeRef.current);
          const textW = ctx.measureText(text).width;
          const pad = 8, bx = 14, by = 14;
          ctx.fillStyle = 'rgba(0,0,0,0.65)';
          ctx.fillRect(bx, by, textW + pad * 2, FONT_SIZE + pad * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fillText(text, bx + pad, by + pad + FONT_SIZE);
          ctx.restore();
        }
      }
      if (hasTwoSources && !secondarySource?.isCameraSource) {
        const v2Now = video2Ref.current;
        if (v2Now && !v2Now.seeking && v2Now.readyState >= 2) {
          ctx.save();
          const FONT_SIZE = 28;
          ctx.font = `bold ${FONT_SIZE}px monospace`;
          const text = formatTimeDetailed(p2CurrentTimeRef.current);
          const textW = ctx.measureText(text).width;
          const pad = 8, bx = PANEL_W + 14, by = 14;
          ctx.fillStyle = 'rgba(0,0,0,0.65)';
          ctx.fillRect(bx, by, textW + pad * 2, FONT_SIZE + pad * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fillText(text, bx + pad, by + pad + FONT_SIZE);
          ctx.restore();
        }
      }

      // Draw PiP camera overlay — circular clip of camera feed composited on top.
      // pip.x is normalized 0–1 across the full panels container, which maps to the
      // full composite canvas width (PANEL_W for single source, PANEL_W*2 for two sources).
      if (pipEnabledRef.current) {
        const pipVid = pipCaptureVideoRef.current;
        if (pipVid && pipVid.readyState >= 2) {
          /* The same clamp the drag applies, so the two cannot diverge even if
             a position arrives from before the box was measured. */
          /**
           * 10% of the container's width on screen (the element is
           * `width: 10%` with aspect-ratio 1), so 5% of it as a radius here.
           * It used to be a flat 110px, which on a single 1280 panel recorded
           * a bubble about 72% larger than the one the coach was framing, and
           * on a 2560 side-by-side canvas recorded a smaller one.
           */
          /* One panel, not the pair: side by side the composite is two 1280
             halves, so the bubble is sized off a half and lands the same
             relative size as it does in single source and on screen. */
          const pipPanelW = hasTwoSources ? composite.width / 2 : composite.width;
          const PIP_R = pipDiameterFor(pipPanelW, composite.height, hasTwoSources) / 2;
          const pip = clampPip(
            pipPosRef.current.x,
            pipPosRef.current.y,
            composite.width,
            panelH,
            PIP_R * 2,
          );
          const cx = pip.x * composite.width;
          const cy = pip.y * panelH;
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, PIP_R, 0, Math.PI * 2);
          ctx.clip();
          // Mirror camera feed horizontally
          ctx.translate(cx * 2, 0);
          ctx.scale(-1, 1);
          // Cover-crop the video to fill the circle without distorting aspect ratio
          const vw = pipVid.videoWidth || 1;
          const vh = pipVid.videoHeight || 1;
          const diameter = PIP_R * 2;
          const scale = Math.max(diameter / vw, diameter / vh);
          const srcW = diameter / scale;
          const srcH = diameter / scale;
          const srcX = (vw - srcW) / 2;
          const srcY = (vh - srcH) / 2;
          ctx.drawImage(pipVid, srcX, srcY, srcW, srcH, cx - PIP_R, cy - PIP_R, diameter, diameter);
          ctx.restore();
          // White border ring
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.85)';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(cx, cy, PIP_R, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
      }

      rafIdRef.current = requestAnimationFrame(drawFrame);
    };

    try {
      // For camera primary source, reuse the camera stream's built-in audio.
      // For other sources, request a separate microphone stream.
      let audioSourceStream: MediaStream;
      if (p1IsCamera && cameraStreamRef.current?.getAudioTracks().length) {
        audioSourceStream = cameraStreamRef.current;
        // Don't assign to micStreamRef — camera stream cleanup is managed by its own useEffect
      } else {
        audioSourceStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        micStreamRef.current = audioSourceStream;
      }

      // Mix audio source with any unmuted video panel audio via Web Audio API
      captureStreamsRef.current = [];
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      const mixedDest = audioCtx.createMediaStreamDestination();
      if (audioSourceStream.getAudioTracks().length) {
        audioCtx.createMediaStreamSource(audioSourceStream).connect(mixedDest);
      }
      if (!p1MutedRef.current && !primarySource.isCameraSource && video1Ref.current) {
        const vs = (video1Ref.current as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream?.();
        if (vs) {
          captureStreamsRef.current.push(vs);
          if (vs.getAudioTracks().length) audioCtx.createMediaStreamSource(vs).connect(mixedDest);
        }
      }
      if (!p2MutedRef.current && secondarySource && !secondarySource.isCameraSource && video2Ref.current) {
        const vs = (video2Ref.current as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream?.();
        if (vs) {
          captureStreamsRef.current.push(vs);
          if (vs.getAudioTracks().length) audioCtx.createMediaStreamSource(vs).connect(mixedDest);
        }
      }

      const combined = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...mixedDest.stream.getAudioTracks(),
      ]);

      // Prefer VP9 (better quality/bitrate ratio) over VP8, fall back to plain webm
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm';

      const recorder = new MediaRecorder(combined, {
        mimeType,
        videoBitsPerSecond: 8_000_000,  // 8 Mbps for crisp 720p composite
        audioBitsPerSecond: 128_000,
      });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        // Stop canvas/combined streams (audio resources released in handleStopRecording)
        combined.getTracks().forEach((t) => t.stop());
        canvasStream.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current = null;

        if (canceledRef.current) {
          const wasAbort = abortRef.current;
          canceledRef.current = false;
          abortRef.current = false;
          if (wasAbort) setIsRecording(false);
          return;
        }

        // Capture thumbnail directly from the composite canvas — synchronous, zero CPU cost.
        // Do NOT load the recording blob into a <video> element: MediaRecorder WebM blobs
        // have duration=Infinity in the header, so Chrome scans the entire blob to resolve
        // the real duration before firing loadedmetadata. For large recordings this causes
        // several seconds of CPU spin immediately after "Stop & Save" is clicked.
        // The composite canvas already holds the last recorded frame, so we draw it scaled
        // down to 160×90 and export as JPEG.
        // Capture thumbnail from composite canvas — works for all source types including
        // camera (video1Ref has srcObject, its last frame is already on the composite canvas).
        let thumbnailDataUrl: string | undefined;
        if (compositeCanvasRef.current) {
          const c = document.createElement('canvas');
          c.width = 160; c.height = 90;
          const ctx2 = c.getContext('2d');
          const src = compositeCanvasRef.current;
          if (ctx2) ctx2.drawImage(src, 0, 0, src.width, src.height, 0, 0, 160, 90);
          thumbnailDataUrl = c.toDataURL('image/jpeg', 0.7);
        }

        const videoBlob = new Blob(chunksRef.current, {
          type: mimeType.split(';')[0],
        });
        const segmentIds = [
          primarySource.segment?.id,
          secondarySource?.segment?.id,
        ].filter(Boolean) as string[];

        const clip: VoiceoverNotableSegmentClip = {
          id: `voiceover-clip-${Date.now()}`,
          insertionPoint: 0, // deprecated — sequence is determined by array position
          isCameraSource: primarySource.isCameraSource,
          segmentIds,
          videoBlob,
          thumbnailDataUrl,
          // Wall-clock duration avoids the expensive blob-scan in getVideoBlobDuration
          videoDuration: (Date.now() - recordingStartMsRef.current) / 1000,
        };

        if (saveModeRef.current === 'continue') {
          onSaveAndContinue(clip);
          setIsRecording(false);
        } else {
          onSave(clip);
          onClose();
        }
      };

      recordingStartMsRef.current = Date.now();
      recorder.start(100);
      mediaRecorderRef.current = recorder;
      rafIdRef.current = requestAnimationFrame(drawFrame);
      setRecordingError(null);
      setIsRecording(true);
    } catch (err) {
      console.error('ClipEditorModal: failed to start recording', err);
      // Stop the canvas stream that was started before the try block
      canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
      canvasStreamRef.current = null;
      const name = (err instanceof DOMException) ? err.name : '';
      let msg: string;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Microphone access was blocked. Open your browser’s site settings, allow the microphone for this site, then try again.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        msg = 'No microphone found. Connect a microphone and try again.';
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        msg = 'Your microphone is in use by another app. Close it and try again.';
      } else if (name === 'OverconstrainedError') {
        msg = 'No microphone matched the required settings. Try a different device.';
      } else if (name === 'SecurityError') {
        msg = 'Recording requires a secure connection (HTTPS). Contact support if this persists.';
      } else {
        msg = 'Recording could not start. Check your microphone and try again.';
      }
      setRecordingError(msg);
    }
  }, [
    isRecording,
    hasTwoSources,
    primarySource,
    secondarySource,
    onSave,
    onSaveAndContinue,
    onClose,
  ]);

  // Stop & save recording (shared by "Save & Close" and "Save & Continue")
  const handleStopRecording = useCallback((mode: 'close' | 'continue' = 'close') => {
    if (!isRecording) return;
    saveModeRef.current = mode;
    canceledRef.current = false;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Pause source videos immediately so they stop decoding while onstop/thumbnail runs
    video1Ref.current?.pause();
    video2Ref.current?.pause();
    // Stop canvas stream tracks BEFORE recorder.stop() — this signals to Chrome's
    // MediaRecorder encoder that the video input has definitively ended, so it can
    // finalize the file without spin-waiting for the next frame (which would never
    // arrive since the RAF loop is already cancelled above).
    canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
    canvasStreamRef.current = null;
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    // Release audio resources synchronously — don't wait for recorder.onstop.
    // getUserMedia echo-cancellation monitors the tab's audio output on a background
    // thread; if we defer cleanup to onstop (async), the monitor keeps running and
    // causes visible CPU spin from the moment "Stop & Save" is clicked.
    captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    captureStreamsRef.current = [];
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    // Stop PiP camera — not the primary/secondary camera streams (managed by their own useEffects).
    // In 'continue' mode the user is immediately recording again, so leave the PiP stream and
    // toggle state intact so the overlay is ready without any re-acquisition delay.
    if (mode !== 'continue') {
      pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      pipCameraStreamRef.current = null;
      pipCaptureVideoRef.current = null;
      pipEnabledRef.current = false;
      setPipEnabled(false);
    }
    // Note: setIsRecording(false) is called inside recorder.onstop for 'continue' mode,
    // and implicitly via onClose for 'close' mode.
  }, [isRecording]);

  /** Stop the current recording without saving it; keep the modal open. */
  const handleAbortRecording = useCallback(() => {
    if (!isRecording) return;
    canceledRef.current = true;
    abortRef.current = true;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    video1Ref.current?.pause();
    video2Ref.current?.pause();
    canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
    canvasStreamRef.current = null;
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    captureStreamsRef.current = [];
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    // pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    // pipCameraStreamRef.current = null;
    // pipCaptureVideoRef.current = null;
    // pipEnabledRef.current = false;
    // setPipEnabled(false);
    // setIsRecording(false) is called in recorder.onstop when abortRef is true
  }, [isRecording]);

  // Cleanup on unmount — stop all streams and cancel any active recording
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
      canvasStreamRef.current = null;
      if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
      captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
      captureStreamsRef.current = [];
      micStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioContextRef.current?.close();
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      secondaryCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      video1Ref.current?.pause();
      video2Ref.current?.pause();
    };
  }, []);

  /* Elapsed recording time. On-screen only — it is never drawn to the
     composite canvas, so it cannot reach the recorded file.
     A ref for the start instant and state for the display, ticking once a
     second: the coach needs to know a recording is long, not to time it to
     the frame. */
  const recordingStartedAtRef = useRef<number | null>(null);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  useEffect(() => {
    if (!isRecording) {
      recordingStartedAtRef.current = null;
      setRecordingElapsed(0);
      return;
    }
    recordingStartedAtRef.current = Date.now();
    setRecordingElapsed(0);
    const id = setInterval(() => {
      const started = recordingStartedAtRef.current;
      if (started !== null) setRecordingElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  /* Bottom right by default; the clamp's own inset puts it 16px off both
     edges, so {1, 1} resolves to exactly that. Dragging moves it from there. */
  const [pipPos, setPipPos] = useState({ x: 1, y: 1 });
  const pipPosRef = useRef(pipPos);
  const pipPosDraggingRef = useRef(false);

  /* One panel's width: half the pair when there are two, the whole box when
     there is one. The composite applies the identical rule. */
  const pipPanelWidth = (videoBox?.width ?? 0) / (hasTwoSources ? 2 : 1);
  /* Paired: videoBox is the whole pair, so the panel is half its width and all
     of its height. Single: videoBox is already the contained picture. */
  const pipDiameter = pipDiameterFor(pipPanelWidth, videoBox?.height ?? 0, hasTwoSources);

  /* Re-clamp on resize: a fraction that was legal in one box can put the
     bubble half outside a smaller one. */
  useEffect(() => {
    if (!videoBox || videoBox.width <= 0 || videoBox.height <= 0) return;
    const next = clampPip(
      pipPosRef.current.x, pipPosRef.current.y,
      videoBox.width, videoBox.height, pipDiameter,
    );
    if (next.x !== pipPosRef.current.x || next.y !== pipPosRef.current.y) {
      pipPosRef.current = next;
      setPipPos(next);
    }
  }, [videoBox, pipDiameter]);

  const measureVideoBox = useCallback(() => {
    const container = panelsContainerRef.current;
    if (!container) return;
    const c = container.getBoundingClientRect();
    if (hasTwoSources) {
      setVideoBox({ left: 0, top: 0, width: c.width, height: c.height });
      return;
    }
    const v = video1Ref.current;
    if (!v || !v.videoWidth || !v.videoHeight) {
      setVideoBox({ left: 0, top: 0, width: c.width, height: c.height });
      return;
    }
    const e = v.getBoundingClientRect();
    const rot = primarySource.rotation ?? 0;
    const swapped = rot === 90 || rot === 270;
    const vw = swapped ? v.videoHeight : v.videoWidth;
    const vh = swapped ? v.videoWidth : v.videoHeight;
    const scale = Math.min(e.width / vw, e.height / vh);
    const w = vw * scale;
    const h = vh * scale;
    setVideoBox({
      left: e.left - c.left + (e.width - w) / 2,
      top: e.top - c.top + (e.height - h) / 2,
      width: w,
      height: h,
    });
  }, [hasTwoSources, primarySource.rotation]);

  useEffect(() => {
    measureVideoBox();
    const container = panelsContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(measureVideoBox);
    ro.observe(container);
    return () => ro.disconnect();
    // p1Duration changes on loadedmetadata, which is when videoWidth appears.
  }, [measureVideoBox, p1Duration, isOpen]);

  // Any position from before the space changed is pulled back into range.

  // Cancel / close
  const handleClose = useCallback(() => {
    canceledRef.current = true;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
    canvasStreamRef.current = null;
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    captureStreamsRef.current = [];
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    pipCameraStreamRef.current = null;
    pipCaptureVideoRef.current = null;
    video1Ref.current?.pause();
    video2Ref.current?.pause();
    setIsRecording(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const inner = (
    <div className={embedded
      ? 'flex flex-col h-full w-full overflow-hidden rounded-xl border border-[var(--editor-border)] shadow-2xl'
      : 'w-full h-full md:w-auto md:h-auto rounded-none md:rounded-xl md:max-w-5xl mx-0 md:mx-auto flex flex-col md:max-h-[95vh] overflow-hidden shadow-2xl border border-[var(--editor-border)]'
    } style={{ background: embedded ? 'var(--editor-sidebar)' : 'var(--editor-bg)' }}>

        {/* ── Header ── */}
        {/* py-2.5, not py-3: the recipe's text buttons are 36 where these were
            32, so trimming 2 of padding a side keeps this header at the 56 it
            was. Header height comes straight out of the video. */}
        <div className="flex items-center gap-3 px-4 py-2.5 flex-shrink-0 flex-wrap" style={{ background: 'var(--editor-header)', borderBottom: '1px solid var(--editor-border)' }}>
          {/* Labelled, and the label changes with what the control will do.
              Idle it is a safe close, so "Done". Mid-recording handleClose sets
              canceledRef, which makes recorder.onstop drop the blob before the
              thumbnail step, so the take is thrown away: "Discard". */}
          {/* Hidden while recording. It used to read "Discard" there and do
              exactly what Abort does, so the take had two ways out with two
              names. Recording now has three exits and one meaning each: Save &
              Continue, Save & Close, and Discard. */}
          {!isRecording && (
          <button
            type="button"
            onClick={handleClose}
            className={EBTN_GHOST}
            title="Close the clip recorder"
            aria-label="Done, close the clip recorder"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="hidden md:inline text-sm">Done</span>
          </button>
          )}
          {/* The segment's name is not repeated here. Each panel's own label
              row carries it, which is the only place it shows in full and the
              only place that works when two panels have different names. */}
          <span className="font-semibold text-[var(--editor-text)] flex-shrink-0">Clip Recorder</span>
          <div className="flex-1" />
          {/* Add side-by-side video — shown only when opened directly via "Open in Editor" shortcut */}
          {/* The same slot does both jobs. It sat empty once a comparison was
              set, which left no way to swap one; the label changes with what
              the press will do. */}
          {onAddSecondarySource && !isRecording && (
            <button
              type="button"
              onClick={onAddSecondarySource}
              className={EBTN_ICON}
              title={hasTwoSources ? 'Change comparison video' : 'Add comparison video'}
              aria-label={hasTwoSources ? 'Change comparison video' : 'Add comparison video'}
            >
              {/* Two-panel split icon */}
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <rect x="2" y="4" width="9" height="16" rx="1" />
                <rect x="13" y="4" width="9" height="16" rx="1" />
                <path strokeLinecap="round" d="M11.5 12h1" />
              </svg>
            </button>
          )}
          {/* Camera overlay checkbox — only when primary source is NOT camera (camera-as-primary already has the camera) */}
          {!primarySource.isCameraSource && (
            <label className="hidden md:flex items-center gap-2 cursor-pointer text-sm text-[var(--editor-text)] flex-shrink-0">
              <div
                className={`relative w-9 h-5 rounded-full transition-colors ${pipEnabled ? 'bg-[var(--color-db-oasis)]' : 'bg-[var(--editor-border)]'}`}
                onClick={(e) => { e.preventDefault(); setPipEnabled(prev => !prev); }}
              >
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${pipEnabled ? 'translate-x-4' : ''}`} />
              </div>
              Camera overlay
            </label>
          )}
          {isRecording && (
            /* On-screen only. Nothing here is drawn to the composite canvas.
               The word is no longer hidden when embedded: embedded is how the
               clip editor is always opened from the lesson editor, so hiding
               it there left an 8px dot as the entire recording indicator. */
            <div
              className="flex items-center gap-2 flex-shrink-0 rounded-full px-2.5 py-1"
              style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)' }}
              title="Recording"
              aria-live="polite"
            >
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-semibold text-red-400">REC</span>
              <span className="text-sm font-semibold text-red-400 tabular-nums">
                {formatTime(recordingElapsed)}
              </span>
            </div>
          )}
          {/* Action buttons */}
          {!isRecording ? (
            <button
              type="button"
              data-testid="start-recording-btn"
              onClick={handleStartRecording}
              className={EBTN_RECORD}
            >
              <div className="w-2 h-2 rounded-full bg-white" />
              Start Recording
            </button>
          ) : (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Filled, then washed, then red: the two that save carry weight
                  in that order and Abort carries none, so which two save is
                  readable without reading the labels. */}
              <button
                type="button"
                onClick={() => handleStopRecording('continue')}
                className={EBTN_PRIMARY}
                title="Save this recording and record another"
              >
                Save &amp; Continue
              </button>
              <button
                type="button"
                data-testid="save-close-recording-btn"
                onClick={() => handleStopRecording('close')}
                className={EBTN_SECONDARY}
                title="Save this recording and close the recorder"
              >
                Save &amp; Close
              </button>
              {/* Separates the two that save from the one that discards. */}
              <div className="w-px h-5 mx-1.5 flex-shrink-0" style={{ background: 'var(--editor-border)' }} />
              <button
                type="button"
                onClick={handleAbortRecording}
                className={EBTN_DESTRUCTIVE}
                title="Discard this recording and stay in the recorder"
              >
                Discard
              </button>
            </div>
          )}
        </div>

        {/* Recording error banner */}
        {recordingError && (
          <div
            role="alert"
            className="flex items-start gap-2.5 px-4 py-2.5 text-sm"
            style={{ background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.3)', color: 'var(--editor-text)' }}
          >
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="flex-1">{recordingError}</span>
            <button
              type="button"
              onClick={() => setRecordingError(null)}
              className="flex-shrink-0 text-[var(--editor-text-muted)] hover:text-[var(--editor-text)] transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Video panels ── */}
        <div
          ref={panelsContainerRef}
          /* pt-2 when paired: 16 of top padding sat under a header that already
             has its own bottom border, which read as a dead band above the
             titles. Single source keeps p-4 exactly. */
          className={`relative flex overflow-hidden flex-1 min-h-0 ${hasTwoSources ? 'px-4 pt-4 pb-4' : 'p-4 gap-4 justify-center'}`}
          /* On-screen only: this is the container the panels sit in, not the
             composite canvas, so none of it reaches the recorded file.
             Deliberately NOT pulsing. The dot in the header pulses, which is
             enough to read as live; a whole border breathing in the coach's
             peripheral vision for a three-minute take is a distraction, and it
             would ignore prefers-reduced-motion. Static red states the fact
             and then stays out of the way. */
          style={isRecording ? {
            boxShadow: 'inset 0 0 0 2px rgb(239, 68, 68), inset 0 0 24px rgba(239, 68, 68, 0.25)',
          } : undefined}
        >
          {/* Each panel is a surface one step above the recorder's background,
              so the pair reads as two panels rather than as one dark field.
              --editor-timeline sits between --editor-sidebar (the embedded
              recorder's own ground) and --editor-surface. */}
          <div
            className="flex-1 min-w-0 min-h-0 flex flex-col p-2 rounded-lg"
            style={hasTwoSources ? { background: 'var(--editor-timeline)' } : undefined}
          >
            <VideoPanel
              source={primarySource}
              videoRef={video1Ref}
              annotationCanvasRef={annotationCanvas1Ref}
              isPlaying={p1IsPlaying}
              currentTime={p1CurrentTime}
              duration={p1Duration}
              speed={p1Speed}
              isMuted={p1Muted}
              onTogglePlay={handleTogglePlay1}
              onSeek={handleSeek1}
              onScrubStart={handleScrubStart1}
              onScrubEnd={handleScrubEnd1}
              onSpeedChange={handleSpeedChange1}
              onToggleMute={handleToggleMute1}
              sideBySide={hasTwoSources}
              onVideoBoxHeight={setDividerHeight}
              ref={panel1Ref}
              drawingTool={drawingTool}
              drawColor={drawColor}
              onSetDrawingTool={setDrawingTool}
              onSetDrawColor={setDrawColor}
              onStrokeCommitted={() => noteStroke(1)}
            />
          </div>

          {/* One rule instead of a gutter. Its height is the video area's, not
              the column's, so it does not run past the pictures into the
              titles or the transports. */}
          {hasTwoSources && secondarySource && (
            /* No padding at all: the panels meet flush and the rule is the only
               thing between them. This gives 16px of width back to the pair
               rather than costing any. */
            <div className="flex-shrink-0 self-stretch flex flex-col justify-center" aria-hidden="true">
              {/* --editor-selected-text, not biscayne. #1A6B8A against a raised
                  panel is barely a step; #7EB4D4 is the lightest value in the
                  editor set and is the only one that reads as a rule at 1px. */}
              <div className="w-px" style={{ background: 'var(--editor-selected-text)', height: dividerHeight }} />
            </div>
          )}
          {hasTwoSources && secondarySource && (
            <div
              className="flex-1 min-w-0 min-h-0 flex flex-col p-2 rounded-lg"
              style={{ background: 'var(--editor-timeline)' }}
            >
              <VideoPanel
                source={secondarySource}
                videoRef={video2Ref}
                annotationCanvasRef={annotationCanvas2Ref}
                isPlaying={p2IsPlaying}
                currentTime={p2CurrentTime}
                duration={p2Duration}
                speed={p2Speed}
                isMuted={p2Muted}
                onTogglePlay={handleTogglePlay2}
                onSeek={handleSeek2}
                onScrubStart={handleScrubStart2}
                onScrubEnd={handleScrubEnd2}
                onSpeedChange={handleSpeedChange2}
                onToggleMute={handleToggleMute2}
                sideBySide
                ref={panel2Ref}
                drawingTool={drawingTool}
                drawColor={drawColor}
                onSetDrawingTool={setDrawingTool}
                onSetDrawColor={setDrawColor}
                onStrokeCommitted={() => noteStroke(2)}
              />
            </div>
          )}

          {/* PiP camera bubble — floats over the full panels area so it can be dragged across both videos */}
          {pipEnabled && (
            <div
              className="absolute rounded-full border-2 border-[var(--editor-border)] shadow-lg overflow-hidden cursor-move z-20 pointer-events-auto"
              /* Sized off one panel, not the box it floats over — in side by
                 side that box is the pair, which is what made the paired bubble
                 twice the single-source size. Position is the coach's, run
                 through the same clamp the composite uses. */
              style={{
                width: pipDiameter,
                aspectRatio: '1',
                left: (videoBox?.left ?? 0) + pipPos.x * (videoBox?.width ?? 0),
                top: (videoBox?.top ?? 0) + pipPos.y * (videoBox?.height ?? 0),
                transform: 'translate(-50%, -50%)',
              }}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                pipPosDraggingRef.current = true;
              }}
              onPointerMove={(e) => {
                if (!pipPosDraggingRef.current) return;
                const container = panelsContainerRef.current;
                if (!container || !videoBox || videoBox.width <= 0 || videoBox.height <= 0) return;
                const c = container.getBoundingClientRect();
                const next = clampPip(
                  (e.clientX - c.left - videoBox.left) / videoBox.width,
                  (e.clientY - c.top - videoBox.top) / videoBox.height,
                  videoBox.width,
                  videoBox.height,
                  pipDiameter,
                );
                setPipPos(next);
                pipPosRef.current = next;
              }}
              onPointerUp={() => { pipPosDraggingRef.current = false; }}
              onPointerLeave={() => { pipPosDraggingRef.current = false; }}
            >
              <video ref={setPipVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
            </div>
          )}
        </div>

        {/* One set of drawing tools for both panels, full width beneath them.
            The pen, the colour and the history act on whichever canvas the
            coach draws on — nothing about stroke routing changes. */}
        {hasTwoSources && secondarySource && (
          <div className="px-4 flex-shrink-0 flex justify-center">
            <DrawToolbar
              fullWidth
              drawingTool={drawingTool}
              setDrawingTool={setDrawingTool}
              drawColor={drawColor}
              setDrawColor={setDrawColor}
              canAnnotate={sharedCanAnnotate}
              undoDepth={historyDepth.undo}
              redoDepth={historyDepth.redo}
              onUndo={handleSharedUndo}
              onRedo={handleSharedRedo}
            />

          </div>
        )}

      </div>
  );

  if (embedded) return inner;
  return (
    <div className="fixed inset-0 md:inset-8 z-[70] flex items-center justify-center bg-black/70 rounded-none md:rounded-xl overflow-hidden">
      <div className="w-full h-full md:w-auto md:h-auto flex items-center justify-center"
        style={{
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {inner}
      </div>
    </div>
  );
}
